// src/hooks/dogDayManagerActions.js
import { loadFromStorage, saveToStorage } from "@/lib/storage";
import {
  USERS,
  INITIAL_DAYS_DAVID,
  INITIAL_DAYS_SARA,
  LOCAL_STORAGE_KEYS,
} from "@/config/constants";
import {
  formatDateKey,
  getDayNameFromDateKey,
  getWeekdayFromDateKey,
  parseDateKey,
  addWeeksToDateKey,
  getWeekDays,
} from "@/lib/dateUtils";
import { saveDogs, saveStates, sanitizeForFirestore } from "@/realtimeDogs";
import {
  removeFromTemplate,
  renameDogInTemplate,
  upsertDogInTemplate,
  updateTemplateOrder,
} from "@/lib/templateSync";
import { reviseRecurringNotes, resolveNoteForDate, isProtectedNoteException } from "@/lib/noteRules";
import { getWorkspaceId } from "@/lib/workspaceUtils";
import { makeEditLockKey } from "@/lib/editLockKey";
import { enqueueDocWrite } from "@/lib/docWriteQueue";
import { auth, db } from "@/firebase";
import { doc, getDoc, getDocFromServer, runTransaction, serverTimestamp } from "firebase/firestore";

import { DELETE_SCOPE } from "@/config/deleteScope";
import { getDogsFor } from "@/lib/dogStateHelpers";
import { generateDogId, getDogIdentity, makeDogStateKey } from "@/lib/dogIdentity";




const deepClone = (obj) => JSON.parse(JSON.stringify(obj));
const MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS = 26; // 6 meses
const MIN_WEEKS_REMAINING_FOR_EXTENSION = 4; // Extender cuando queden menos de 4 semanas

// Feature flag: desactivar propagación automática de ventana
const ENABLE_AUTO_WINDOW_EXTENSION = false;

/* --------------------------- Datos iniciales día --------------------------- */
export const getInitialDayDataForUser = (
  dayKeyForInitial,
  selectedUser,
  permanentlyDeletedDogsParam = []
) => {
  // hot-fix: sin semillas en runtime, Firestore es la fuente de verdad
  return [];
};

/* -------------------------------- Historial -------------------------------- */
export const recordSnapshot = (
  currentDaysList,
  currentCompletedDogs,
  currentUrgentDogs,
  history,
  currentPosition,
  setHistory,
  setCurrentPosition,
  MAX_HISTORY_LENGTH
) => {
  const snapshot = {
    daysList: deepClone(currentDaysList),
    completedDogs: deepClone(currentCompletedDogs),
    urgentDogs: deepClone(currentUrgentDogs),
  };

  const newHistory = history.slice(0, currentPosition + 1);
  let updatedHistory = [...newHistory, snapshot];

  if (updatedHistory.length > MAX_HISTORY_LENGTH) {
    updatedHistory = updatedHistory.slice(
      updatedHistory.length - MAX_HISTORY_LENGTH
    );
  }

  setHistory(updatedHistory);
  setCurrentPosition(updatedHistory.length - 1);
};

export const handleUndo = (
  currentPosition,
  history,
  setDaysList,
  setCompletedDogs,
  setUrgentDogs,
  setCurrentPosition,
  toast
) => {
  if (currentPosition > 0) {
    const newPosition = currentPosition - 1;
    const previousState = history[newPosition];
    setDaysList(deepClone(previousState.daysList));
    setCompletedDogs(deepClone(previousState.completedDogs));
    setUrgentDogs(deepClone(previousState.urgentDogs));
    setCurrentPosition(newPosition);
    toast({
      title: "Acción deshecha",
      description: "Se ha restaurado el estado anterior.",
    });
  }
};

export const handleRedo = (
  currentPosition,
  history,
  setDaysList,
  setCompletedDogs,
  setUrgentDogs,
  setCurrentPosition,
  toast
) => {
  if (currentPosition < history.length - 1) {
    const newPosition = currentPosition + 1;
    const nextState = history[newPosition];
    setDaysList(deepClone(nextState.daysList));
    setCompletedDogs(deepClone(nextState.completedDogs));
    setUrgentDogs(deepClone(nextState.urgentDogs));
    setCurrentPosition(newPosition);
    toast({
      title: "Acción rehecha",
      description: "Se ha restaurado el siguiente estado.",
    });
  }
};

/* ----------------------------- Cambio de usuario ---------------------------- */
export const handleUserChange = (
  selectedUser,
  setSelectedUser,
  setCurrentDate,
  setSelectedDayKey,
  toast,
  profiles
) => {
  const currentIndex = profiles.findIndex((p) => p.name === selectedUser);
  const nextIndex = (currentIndex + 1) % profiles.length;
  const newUser = profiles[nextIndex].name;

  setSelectedUser(newUser);
  const newCurrentDate = loadFromStorage(
    LOCAL_STORAGE_KEYS.CURRENT_DATE,
    new Date().toISOString(),
    newUser
  );
  const dateObj = new Date(newCurrentDate);
  setCurrentDate(dateObj);
  setSelectedDayKey(formatDateKey(dateObj));
  toast({
    title: "Usuario cambiado",
    description: `Ahora estás viendo la lista de ${newUser}.`,
  });
};

/* -------------------------- Marcar/limpiar completados -------------------------- */
export const handleClearAll = (
  daysList,
  selectedDayKey,
  completedDogs,
  urgentDogs,
  setCompletedDogs,
  setUrgentDogs,
  recordSnapshotFunc,
  toast,
  getInitialDayData,
  selectedUser
) => {
  const newCompletedDogs = { ...completedDogs };
  const dogsForCurrentDay = getDogsFor(daysList, selectedUser, selectedDayKey);

  dogsForCurrentDay.forEach((dog) => {
    // Fase 2: clave de estado por identidad estable (id con fallback legacy
    // por nombre normalizado), en vez de siempre por nombre.
    const dogKey = makeDogStateKey(selectedDayKey, dog);
    if (newCompletedDogs[dogKey]) newCompletedDogs[dogKey] = false;
  });
  setCompletedDogs(newCompletedDogs);
  recordSnapshotFunc(daysList, newCompletedDogs, urgentDogs);
};

export const handleCheckAll = (
  daysList,
  selectedDayKey,
  completedDogs,
  urgentDogs,
  setCompletedDogs,
  setUrgentDogs,
  recordSnapshotFunc,
  toast,
  getInitialDayData,
  selectedUser
) => {
  const newCompletedDogs = { ...completedDogs };
  const dogsForCurrentDay = getDogsFor(daysList, selectedUser, selectedDayKey);

  dogsForCurrentDay.forEach((dog) => {
    // Fase 2: clave de estado por identidad estable (id con fallback legacy
    // por nombre normalizado), en vez de siempre por nombre.
    const dogKey = makeDogStateKey(selectedDayKey, dog);
    if (!urgentDogs[dogKey]) newCompletedDogs[dogKey] = true;
  });
  setCompletedDogs(newCompletedDogs);
  recordSnapshotFunc(daysList, newCompletedDogs, urgentDogs);
};

/* -------------------------- Clicks (verde/urgente) -------------------------- */
export const handleDogClick = (
  selectedDayKey,
  dogName,
  completedDogs,
  urgentDogs,
  setCompletedDogs,
  setUrgentDogs,
  daysList,
  recordSnapshotFunc,
  dogId // Fase 2: identidad estable opcional, prioridad sobre nombre si se provee
) => {
  // Fase 2: clave de estado por identidad estable (id con fallback legacy
  // por nombre normalizado). makeDogStateKey acepta un objeto tipo dog;
  // aquí solo tenemos dogName/dogId sueltos, así que se construye uno mínimo.
  const key = makeDogStateKey(selectedDayKey, { id: dogId, name: dogName });
  const newCompletedDogs = { ...completedDogs, [key]: !completedDogs[key] };
  const newUrgentDogs = { ...urgentDogs };
  if (newCompletedDogs[key]) delete newUrgentDogs[key];
  setCompletedDogs(newCompletedDogs);
  setUrgentDogs(newUrgentDogs);
  recordSnapshotFunc(daysList, newCompletedDogs, newUrgentDogs);
};

export const handleUrgentClick = (
  selectedDayKey,
  dogName,
  urgentDogs,
  completedDogs,
  setUrgentDogs,
  setCompletedDogs,
  daysList,
  recordSnapshotFunc,
  dogId // Fase 2: identidad estable opcional, prioridad sobre nombre si se provee
) => {
  const key = makeDogStateKey(selectedDayKey, { id: dogId, name: dogName });
  const newUrgentDogs = { ...urgentDogs, [key]: !urgentDogs[key] };
const newCompletedDogs = { ...completedDogs };

if (newUrgentDogs[key]) delete newCompletedDogs[key];

setUrgentDogs(newUrgentDogs);
setCompletedDogs(newCompletedDogs);
  recordSnapshotFunc(daysList, newCompletedDogs, newUrgentDogs);
};

/* ----------------------- Helpers de Persistencia Inmediata ---------------------- */

/**
 * Helper para mergear un perro a una lista sin duplicar.
 * `matcher` puede ser:
 *  - un string: nombre a buscar (comportamiento original)
 *  - una función (d) => boolean: matcher por identidad (id o nombre antiguo)
 * Esto permite localizar el registro existente por identidad estable (id)
 * cuando está disponible, en vez de depender solo del nombre.
 */
const mergeDogIntoList = (existingList, newDog, matcher) => {
  const matchFn = typeof matcher === "function"
    ? matcher
    : (d) => d.name === (matcher ?? newDog.name);
  const idx = existingList.findIndex(matchFn);
  if (idx !== -1) {
    // Actualizar existente (renombra si newDog.name difiere del original)
    return existingList.map((d, i) => i === idx ? { ...d, ...newDog } : d);
  } else {
    // Append nuevo
    return [...existingList, { ...newDog }];
  }
};


/**
 * Calcula los dayKeys a tocar al añadir un perro (reutiliza lógica de PDF)
 */
export const getTargetDayKeysForDogAdd = ({
  daysKeys,  // días base seleccionados
  isFixed,
  firstAddedDateKey,
  maxWeeksAhead = MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS
}) => {
  const targets = [];
  
  if (isFixed) {
    // Perro fijo: propagar a 26 semanas
    daysKeys.forEach((dateKey) => {
      for (let i = 0; i < maxWeeksAhead; i++) {
        const targetDateKey = addWeeksToDateKey(dateKey, i);
        if (parseDateKey(targetDateKey) >= parseDateKey(firstAddedDateKey)) {
          targets.push(targetDateKey);
        }
      }
    });
  } else {
    // Perro no fijo: solo días seleccionados
    targets.push(...daysKeys);
  }
  
  // Deduplicar
  return Array.from(new Set(targets));
};

/**
 * Persiste inmediatamente a Firestore (async)
 * Logs agresivos para diagnosticar por qué no persiste
 */
export const persistAddedDogToFirebase = async ({
  workspaceId,        // workspace compartido canónico (desde ConfigContext)
  mergedMetaByDayKey,  // { dayKey: { dogs, existedBefore }, ... }
  targetDayKeys,      // array de dayKeys
  profile,
  selectedUser,
  lastSavedDaysListRef,
  newDogData,         // para mergear en targets futuros
  sourceDaysByWeekday // { weekday: [dogs de referencia], ... }
}) => {
  try {
    
    // LOG DURO: SIEMPRE log de inicio para saber si entra
    console.error('[ADD_DOG_PERSIST_START]', JSON.stringify({
      workspaceId: workspaceId || null,
      profile,
      selectedUser,
      targetsCount: Array.isArray(targetDayKeys) ? targetDayKeys.length : 0,
      targetsPreview: Array.isArray(targetDayKeys) ? targetDayKeys.slice(0, 10) : [],
      hasLastSavedRef: !!lastSavedDaysListRef?.current
    }));

    if (!workspaceId) {
      console.error('[ADD_DOG_PERSIST] No workspaceId - skipping persistence');
      return;
    }

    if (!Array.isArray(targetDayKeys) || targetDayKeys.length === 0) {
      console.error('[ADD_DOG_PERSIST] No targets - skipping persistence');
      return;
    }

    console.error('[ADD_DOG_TARGETS]', JSON.stringify({
      profile,
      selectedUser,
      count: targetDayKeys.length,
      firstFive: targetDayKeys.slice(0, 5)
    }));

    for (const dayKey of targetDayKeys) {
      const k = `${profile}::${dayKey}`;
      const meta = mergedMetaByDayKey?.[dayKey] || { dogs: null, existedBefore: false };
      let dogsToWrite = [];
      let remoteLen = 0;
      let mergedLen = 0;
      let isLocalOrRemote = 'local';

      try {
        // LOG: Rama de decisión (local vs remote)
        console.error('[PROPAGATE_BRANCH]', JSON.stringify({
          dayKey,
          existedBefore: meta.existedBefore,
          localLen: meta.dogs?.length ?? null,
          willUseRemote: !meta.existedBefore
        }));

        // Decidir si usar local o hacer read-before-write
        if (meta.existedBefore && meta.dogs) {
          // Caso 1: Estaba en prevDaysList (semana ya cargada)
          dogsToWrite = meta.dogs || [];
          mergedLen = dogsToWrite.length;
          isLocalOrRemote = 'local';
          
          console.error('[PROPAGATE_MERGE_LOCAL]', JSON.stringify({
            dayKey,
            profile,
            dogsLen: mergedLen
          }));
        } else {
          // Caso 2: NO estaba en prevDaysList (semana futura no cargada) → forzar read-before-write
          const docRef = doc(db, `workspaces/${workspaceId}/days/${dayKey}/profiles/${profile}`);
          const docSnap = await getDoc(docRef);
          
          let remoteExisting = [];
          if (docSnap.exists()) {
            const data = docSnap.data();
            remoteExisting = data?.dogs || [];
          }
          
          remoteLen = remoteExisting.length;
          
          // ✅ ORDEN DE REFERENCIA: Aplicar el orden final de la semana editada
          // Esto garantiza que las semanas futuras replican exactamente el mismo orden
          const sourceDogs = sourceDaysByWeekday?.[getDayNameFromDateKey(dayKey)] ?? [];
          const sourceOrder = new Map(sourceDogs.map((d, i) => [d.name, i]));
          
          // Perros remotos que aparecen en la referencia → ordenar por orden de referencia
          const remoteInSource = remoteExisting
            .filter(d => sourceOrder.has(d.name))
            .sort((a, b) => sourceOrder.get(a.name) - sourceOrder.get(b.name));
          
          // Perros remotos que NO aparecen en la referencia → preservar al final, no borrar
          const remoteNotInSource = remoteExisting
            .filter(d => !sourceOrder.has(d.name));
          
          // Si el nuevo perro no está ya en remoto, insertarlo en su posición de referencia
          const alreadyPresent = remoteExisting.some(d => d.name === newDogData.name);
          if (!alreadyPresent) {
            const newDogIdx = sourceOrder.get(newDogData.name) ?? Infinity;
            let insertAt = remoteInSource.findIndex(d => sourceOrder.get(d.name) > newDogIdx);
            if (insertAt === -1) insertAt = remoteInSource.length;
            remoteInSource.splice(insertAt, 0, { ...newDogData });
          }
          
          // Lista final: referencia + remotos no conocidos (preservados)
          dogsToWrite = [...remoteInSource, ...remoteNotInSource];
          mergedLen = dogsToWrite.length;
          isLocalOrRemote = 'remote-with-source-order';
          
          console.error('[PROPAGATE_MERGE]', JSON.stringify({
            dayKey,
            profile,
            remoteLen,
            sourceLen: sourceDogs.length,
            mergedLen,
            newDog: newDogData?.name || '?',
            remoteInSourceLen: remoteInSource.length,
            remoteNotInSourceLen: remoteNotInSource.length
          }));
        }

        console.error('[ADD_DOG_PERSIST_BEFORE]', JSON.stringify({
          profile,
          dayKey,
          key: k,
          dogsLen: dogsToWrite.length,
          source: isLocalOrRemote
        }));

        await saveDogs(workspaceId, dayKey, dogsToWrite, profile);
        
        // Actualizar loop prevention CON COMPOSITE KEY SOLO si ref existe
        if (lastSavedDaysListRef?.current) {
          lastSavedDaysListRef.current[k] = dogsToWrite;
        }
        
        console.error('[ADD_DOG_PERSIST_OK]', JSON.stringify({
          profile,
          dayKey,
          key: k,
          dogsLen: dogsToWrite.length,
          source: isLocalOrRemote
        }));
      } catch (e) {
        console.error('[ADD_DOG_PERSIST_ERR]', JSON.stringify({
          dayKey,
          message: e?.message || String(e),
          code: e?.code || null,
          stack: e?.stack || null
        }));
      }
    }
  } catch (e) {
    console.error('[ADD_DOG_PERSIST_FATAL]', JSON.stringify({
      message: e?.message || String(e),
      code: e?.code || null,
      stack: e?.stack || null
    }));
  }
};

/* --------------------------- AÑADIR PERRO (ÚNICA) --------------------------- */
export const handleAddDogToList = (
  newDogName,
  newDogWhatsApp,
  dogDaysAsDateKeys,
  dogCount,
  isFixed,
  daysList,
  completedDogs,
  urgentDogs,
  setDaysList,
  recordSnapshotFunc,
  toast,
  getInitialDayData,
  currentSelectedDayKey,
  selectedUser,
  locationUrl,
  lastSavedDaysListRef,
  workspaceId  // ✅ workspace compartido canónico desde ConfigContext
) => {
  // Normalizaciones seguras
  const name =
    typeof newDogName === "string" ? newDogName.trim() : "";
  if (!name) return false;

  const wa =
    typeof newDogWhatsApp === "string" ? newDogWhatsApp.trim() : "";

  const location =
    typeof locationUrl === "string" ? locationUrl.trim() : "";

  const daysKeys =
    Array.isArray(dogDaysAsDateKeys) && dogDaysAsDateKeys.length > 0
      ? dogDaysAsDateKeys
      : [currentSelectedDayKey];

  // LOG: Entrada al handler
  console.error('[ADD_DOG_ENTER]');

  const count = Number.isFinite(dogCount) && dogCount > 0 ? dogCount : 1;
  const fixed = Boolean(isFixed);

  // Fecha de primera presencia
  const firstAddedDateKeyForThisDog =
    daysKeys
      .slice()
      .sort(
        (a, b) => parseDateKey(a).getTime() - parseDateKey(b).getTime()
      )[0] || currentSelectedDayKey || formatDateKey(new Date());

  const newDogData = {
    // Identidad estable: se genera UNA vez al crear el cliente y nunca se
    // regenera al editar/renombrar/sincronizar. Es la clave que permite
    // cambiar el nombre sin romper el vínculo con notas/horarios/estados.
    id: generateDogId(),
    name: name.toUpperCase(),
    whatsappGroup: wa || null,
    locationUrl: location || null,
    count,
    isFixed: fixed,
    originalOrder: Date.now(),
    firstAddedDateKey: firstAddedDateKeyForThisDog,
    informativeNote: "",
  };


  console.log("[DEBUG_ADD_DOG] BEFORE setDaysList", {
    dogName: newDogData.name,
    daysKeys: daysKeys,
    selectedUser: selectedUser,
    currentDaysListKeys: Object.keys(daysList || {}).slice(0, 5)
  });

  // CRÍTICO: Usar functional update para setDaysList para asegurar que se 
  // acumulan los cambios cuando se llama múltiples veces en un loop (ej: PDF import)
  setDaysList((prevDaysList) => {
    let newDaysList = prevDaysList ? deepClone(prevDaysList) : {};

    // Si estaba en eliminados permanentes, lo sacamos
    let permanentlyDeletedDogsRaw = loadFromStorage(
      LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS,
      [],
      selectedUser
    );
    let permanentlyDeletedDogs = Array.isArray(permanentlyDeletedDogsRaw)
      ? permanentlyDeletedDogsRaw
      : [];

    if (permanentlyDeletedDogs.includes(newDogData.name)) {
      permanentlyDeletedDogs = permanentlyDeletedDogs.filter(
        (n) => n !== newDogData.name
      );
      saveToStorage(
        LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS,
        permanentlyDeletedDogs,
        selectedUser
      );
    }

    const processDateKey = (targetDateKey, dogData) => {
      // 🔒 COMPOSITE KEY: Siempre usar profile::dayKey
      const k = `${selectedUser}::${targetDateKey}`;
      
      // ✅ GUARD: Si esta semana no estaba cargada en memoria local, no escribir aquí.
      // La persistencia a Firestore (read-before-write) la gestiona correctamente con el orden de referencia.
      // Sin este guard, sobrescribimos con [newDogData] incompleto, perdiendo perros remotos.
      const wasLoadedLocally =
        Array.isArray(prevDaysList?.[k]) ||
        Array.isArray(prevDaysList?.[targetDateKey]);
      if (!wasLoadedLocally) return;
      
      // Leer existentes: primero composite, luego legacy (por si hay transición)
      const existing = newDaysList?.[k] ?? newDaysList?.[targetDateKey] ?? [];
      const beforeLen = existing.length;

      // Merge: buscar si el perro ya existe
      const idx = existing.findIndex((d) => d.name === dogData.name);
      let merged;
      
      if (idx !== -1) {
        // Actualizar existente
        merged = existing.map((d, i) => 
          i === idx ? { ...d, ...dogData } : d
        );
      } else {
        // Append nuevo
        merged = [
          ...existing,
          { ...dogData, originalOrder: Date.now() + Math.random() }
        ];
      }

      // Ordenar
      merged.sort((a, b) => (a.originalOrder || 0) - (b.originalOrder || 0));

      // Log temporal para validar merge vs overwrite
      console.error('[ADD_DOG_WRITE]', JSON.stringify({
        profile: selectedUser,
        dayKey: targetDateKey,
        key: k,
        beforeLen,
        afterLen: merged.length,
        added: dogData?.name,
        isCompositeKey: true,
        operation: idx !== -1 ? 'update' : 'append'
      }));

      // Escribir SIEMPRE con composite key
      newDaysList[k] = merged;
    };

    // Calcular plan de propagación ANTES de procesar
    const todayDate = new Date();
    const todayKey = formatDateKey(todayDate);
    const todayDow = todayDate.getDay(); // 0=Sun, 1=Mon, 6=Sat
    const todayDowStr = todayDow === 0 || todayDow === 6 ? `${todayDow === 0 ? 'Sun' : 'Sat'}(${todayDow})` : todayDow;
    
    // Calcular lunes de esta semana (startWeekMondayKey)
    const todayWeekMonday = new Date(todayDate);
    todayWeekMonday.setDate(todayDate.getDate() - (todayDate.getDay() === 0 ? 6 : todayDate.getDay() - 1));
    const startWeekMondayKey = formatDateKey(todayWeekMonday);
    
    // Calcular lunes de la semana real desde donde empiezas
    // IMPORTANTE: Si hoy es Sábado (6) o Domingo (0), fuerza el próximo lunes (semana siguiente)
    let firstTargetWeekMondayKey;
    if (todayDow === 0 || todayDow === 6) {
      // Si es fin de semana, usar próximo lunes
      firstTargetWeekMondayKey = addWeeksToDateKey(startWeekMondayKey, 1);
    } else {
      // Si es L-V, calcular lunes de la semana del primer día seleccionado
      const firstStartDate = parseDateKey(daysKeys[0]);
      const firstStartWeekMonday = new Date(firstStartDate);
      firstStartWeekMonday.setDate(firstStartDate.getDate() - (firstStartDate.getDay() === 0 ? 6 : firstStartDate.getDay() - 1));
      firstTargetWeekMondayKey = formatDateKey(firstStartWeekMonday);
    }
    
    const selectedDayNames = daysKeys.map(dk => getDayNameFromDateKey(dk)).filter((d, i, arr) => arr.indexOf(d) === i);
    const computedTargets = {};
    const targetsPreview = [];
    
    if (fixed) {
      daysKeys.forEach((dateKey) => {
        for (let i = 0; i < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; i++) {
          const targetDateKey = addWeeksToDateKey(dateKey, i);
          if (parseDateKey(targetDateKey) < parseDateKey(newDogData.firstAddedDateKey)) continue;
          const dayName = getDayNameFromDateKey(targetDateKey);
          computedTargets[dayName] = (computedTargets[dayName] || 0) + 1;
          if (targetsPreview.length < 10) targetsPreview.push(targetDateKey);
        }
      });
    } else {
      daysKeys.forEach(dateKey => {
        const dayName = getDayNameFromDateKey(dateKey);
        computedTargets[dayName] = (computedTargets[dayName] || 0) + 1;
        if (targetsPreview.length < 10) targetsPreview.push(dateKey);
      });
    }
    
    // LOG: Plan de propagación (JSON.stringify para Logcat legible)
    console.info('[PROPAGATE_PLAN] ' + JSON.stringify({
      todayKey,
      todayDow: todayDowStr,
      startWeekMondayKey,
      firstTargetWeekMondayKey,
      firstAddedDateKey: newDogData.firstAddedDateKey,
      isFixed: newDogData.isFixed,
      selectedDays: selectedDayNames.join(','),
      weeksForward: fixed ? MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS : 0,
      computedTargets,
      targetsPreview
    }));

    // Aplica días y (si es fijo) propaga a semanas futuras
    daysKeys.forEach((dateKey) => {
      if (fixed) {
        for (let i = 0; i < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; i++) {
          const targetDateKey = addWeeksToDateKey(dateKey, i);
          if (
            parseDateKey(targetDateKey) <
            parseDateKey(newDogData.firstAddedDateKey)
          )
            continue;
          processDateKey(targetDateKey, newDogData);
        }
      } else {
        processDateKey(dateKey, newDogData);
      }
    });

    console.log("[DEBUG_ADD_DOG] AFTER setDaysList", {
      dogName: newDogData.name,
      newDaysListKeys: Object.keys(newDaysList).slice(0, 5),
      newDaysListLength: Object.keys(newDaysList).length
    });

    // 🔥 PERSISTENCIA INMEDIATA A FIRESTORE (dentro del updater, con newDaysList real)
    const targetDayKeys = getTargetDayKeysForDogAdd({
      daysKeys,
      isFixed: newDogData.isFixed,
      firstAddedDateKey: newDogData.firstAddedDateKey
    });

    // LOG CALL SITE: Siempre loguea si se llega aquí
    console.error('[ADD_DOG_CALL_SITE]', JSON.stringify({
      workspaceId: workspaceId || null,
      targetDayKeysLen: targetDayKeys?.length || 0,
      willCallPersist: !!workspaceId && (targetDayKeys?.length || 0) > 0
    }));

    if (workspaceId && targetDayKeys?.length > 0) {
      // ✅ Construir mapa weekday → lista completa post-merge de la semana editada
      // Este mapa será la referencia para ordenar perros en semanas futuras no cargadas
      const sourceDaysByWeekday = {};
      daysKeys.forEach(dk => {
        const dayName = getDayNameFromDateKey(dk);
        const k = `${selectedUser}::${dk}`;
        // Usar la lista final de memoria (ya con merge + sort correcto)
        sourceDaysByWeekday[dayName] = newDaysList[k] ?? [];
      });

      // Construir mergedMetaByDayKey con flag existedBefore para evitar "local contaminado"
      const mergedMetaByDayKey = {};
      targetDayKeys.forEach(dayKey => {
        const k = `${selectedUser}::${dayKey}`;
        
        // ✅ CRÍTICO: Marcar si ese dayKey ya existía en prevDaysList con datos reales (length > 0)
        // Placeholders ([]) deben forzar read-before-write
        const arr = prevDaysList?.[k] ?? prevDaysList?.[dayKey];
        const existedBefore = Array.isArray(arr) && arr.length > 0;
        
        // Si existía before con datos reales, guardar los dogs locales (ya tienen merge correcto)
        // Si NO existía o está vacío, pasar null para forzar read-before-write
        const dogsLocal = existedBefore ? (newDaysList[k] ?? []) : null;
        
        mergedMetaByDayKey[dayKey] = {
          dogs: dogsLocal,
          existedBefore
        };
      });

      // Fire-and-forget wrapped en queueMicrotask para garantizar ejecución
      queueMicrotask(() => {
        persistAddedDogToFirebase({
          workspaceId,  // ✅ workspace compartido canónico desde ConfigContext
          mergedMetaByDayKey,
          targetDayKeys,
          profile: selectedUser,
          selectedUser,
          lastSavedDaysListRef,
          newDogData,
          sourceDaysByWeekday,  // ✅ REFERENCIA: orden final de la semana editada
        }).catch(e => console.error('[ADD_DOG_PERSIST_CATCH]', e));
      });

      // 🔥 Alta manual de cliente fijo: escribir/actualizar en la plantilla
      // (upsertDogInTemplate) para que futuras semanas no cargadas nazcan ya
      // con este cliente y su identidad estable. Si no es fijo, no debe
      // alimentar la plantilla (regla: solo fijos participan en templateDogs).
      if (newDogData.isFixed) {
        queueMicrotask(() => {
          upsertDogInTemplate(workspaceId, selectedUser, {
            id: newDogData.id,
            name: newDogData.name,
            originalOrder: newDogData.originalOrder,
            whatsappGroup: newDogData.whatsappGroup,
            locationUrl: newDogData.locationUrl,
            schedules: [],
            count: newDogData.count,
            isFixed: true,
          }).catch(e => console.error('[ADD_DOG_TEMPLATE_ERR]', e?.message));
        });
      }
    }

    return newDaysList;
  });
  recordSnapshotFunc();
  toast({
    title: "Cliente añadido",
    duration: 2000,
  });
  return true;
};


/* ----------------------- Helper: Generar targets de borrado ---------------------- */
/**
 * Calcula los dayKeys futuros para DELETE_SCOPE.ALL
 * Genera targets por múltiplos de 7 días desde selectedDayKey
 * Incluye hoy (i=0) hasta 26 semanas = 27 targets totales
 */
export const getFutureWeekdayKeys = (selectedDayKey, weeks = 26) => {
  const targets = [];
  const startDate = parseDateKey(selectedDayKey);
  
  // Generar desde hoy (i=0) hasta 26 semanas (i=26) = 27 targets
  for (let i = 0; i <= weeks; i++) {
    const targetDate = new Date(startDate);
    targetDate.setDate(targetDate.getDate() + (i * 7)); // +0, +7, +14, ..., +182 días
    const targetDateKey = formatDateKey(targetDate);
    targets.push(targetDateKey);
  }
  
  return targets;
};

/**
 * Normaliza un nombre para comparación case-insensitive
 */
const normalizeDogName = (name) => (name || "").trim().toUpperCase();

/* ----------------------------- ELIMINAR / EDITAR ----------------------------- */
export const handleDeleteDog = async (
  dogName,
  daysList,
  completedDogs,
  urgentDogs,
  selectedDayKey,
  setDaysList,
  setCompletedDogs,
  setUrgentDogs,
  recordSnapshotFunc,
  toast,
  selectedUser,
  scope,
  workspaceId,
  markDeletedAll,
  clearDeletedAll,
  dogId // Fase 2: identidad estable opcional, prioridad sobre nombre si se provee
) => {
  const newDaysList = { ...daysList };
  const newCompletedDogs = { ...completedDogs };
  const newUrgentDogs = { ...urgentDogs };
  const normalizedDogName = normalizeDogName(dogName);
  // Matcher por identidad: si hay dogId, comparar por id (con fallback a
  // nombre para registros legacy sin id); si no hay dogId, comparar solo
  // por nombre normalizado (comportamiento anterior, sin romper nada).
  const matchesTargetDog = (d) =>
    dogId ? (d.id ? d.id === dogId : normalizeDogName(d.name) === normalizedDogName)
          : normalizeDogName(d.name) === normalizedDogName;


  // DEBUG: Inspeccionar scope y tipo
  console.error("[DEL_SCOPE_DBG]", JSON.stringify({
    scope,
    scopeType: typeof scope,
    DELETE_SCOPE,
    normalizedScope: String(scope).toLowerCase().trim(),
    timestamp: new Date().toISOString()
  }));

  // LOG [DEL_ENTER]: Entrada
  console.error("[DEL_ENTER]", JSON.stringify({
    dogName,
    normalizedDogName,
    scope,
    selectedDayKey,
    selectedUser,
    timestamp: new Date().toISOString()
  }));

  const normalizedScope = String(scope).toLowerCase().trim();
  
  if (normalizedScope === DELETE_SCOPE.TODAY) {
    // ✅ BORRADO HOY: Solo borrar del selectedDayKey usando composite key
    const compositeKey = `${selectedUser}::${selectedDayKey}`;
    
    if (newDaysList[compositeKey] && Array.isArray(newDaysList[compositeKey])) {
      const beforeLen = newDaysList[compositeKey].length;
      newDaysList[compositeKey] = newDaysList[compositeKey].filter(
        (dog) => !matchesTargetDog(dog)
      );
      const afterLen = newDaysList[compositeKey].length;

      
      // ✅ MARCAR deletedAll SOLO si quedó vacío
      if (afterLen === 0 && beforeLen > 0) {
        markDeletedAll(selectedUser, selectedDayKey);
        console.error("[MARK_DELETED_ALL_TODAY]", JSON.stringify({
          compositeKey,
          profile: selectedUser,
          dayKey: selectedDayKey,
          beforeLen,
          afterLen,
          reason: "day-emptied-by-delete"
        }));
      }
    }
    
    // Limpiar completedDogs y urgentDogs SOLO de este día
    // completedDogs/urgentDogs usan keys: dateKey-identidad (sin profile).
    // Se limpia tanto la clave legacy por nombre como la clave estable por
    // id (si se conoce) para no dejar estados zombie bajo ninguna de las dos.
    delete newCompletedDogs[`${selectedDayKey}-${dogName}`];
    delete newUrgentDogs[`${selectedDayKey}-${dogName}`];
    if (dogId) {
      delete newCompletedDogs[`${selectedDayKey}-${dogId}`];
      delete newUrgentDogs[`${selectedDayKey}-${dogId}`];
    }

    console.error("[DEL_UI] DELETE_TODAY_DONE", JSON.stringify({
      dogName,
      normalizedDogName,
      selectedDayKey,
      compositeKey,
      daysAffected: 1
    }));

    // Snapshot ANTES de setters para capturar estado nuevo
    recordSnapshotFunc(newDaysList, newCompletedDogs, newUrgentDogs);
    setDaysList(newDaysList);
    setCompletedDogs(newCompletedDogs);
    setUrgentDogs(newUrgentDogs);
  } else if (normalizedScope === DELETE_SCOPE.ALL) {
    // ✅ BORRADO TODOS: for...of await DIRECTO (secuencial, sin IIFE)
    
    // Paso 1: dayKeys exactos donde el perro está cargado en memoria
    // (matchesTargetDog: por id si se conoce, con fallback legacy por nombre)
    const loadedDayKeys = Object.keys(daysList)
      .filter(k => k.startsWith(`${selectedUser}::`))
      .filter(k => {
        const arr = daysList[k];
        return Array.isArray(arr) && arr.some(matchesTargetDog);
      })
      .map(k => k.split('::')[1]);


    // Paso 2: detectar weekdays únicos del perro + proyectar 26 semanas
    // Cubre semanas futuras no cargadas en memoria
    const dogWeekdays = new Set(loadedDayKeys.map(dk => getDayNameFromDateKey(dk)));
    const targetDayKeysSet = new Set(loadedDayKeys);
    if (dogWeekdays.size > 0) {
      getWeekDays(parseDateKey(selectedDayKey)).map(formatDateKey)
        .filter(dk => dogWeekdays.has(getDayNameFromDateKey(dk)))
        .forEach(baseDayKey => {
          for (let i = 0; i <= 26; i++) {
            targetDayKeysSet.add(addWeeksToDateKey(baseDayKey, i));
          }
        });
    } else {
      // fallback si el perro no está en ningún día cargado
      for (let i = 0; i <= 26; i++) targetDayKeysSet.add(addWeeksToDateKey(selectedDayKey, i));
    }
    const targetDayKeys = Array.from(targetDayKeysSet).sort();
    
    console.error("[DEL_TARGETS]", JSON.stringify({
      selectedDayKey,
      loadedDayKeysCount: loadedDayKeys.length,
      dogWeekdaysCount: dogWeekdays.size,
      targetsCount: targetDayKeys.length,
      targetsPreview: targetDayKeys.slice(0, 10),
      weeksInclusive: 26
    }));


    // Actualizar state (local) para todos los targets
    let daysAffected = 0;
    targetDayKeys.forEach(dayKey => {
      const k = `${selectedUser}::${dayKey}`;
      if (newDaysList[k] && Array.isArray(newDaysList[k])) {
        const before = newDaysList[k].length;
        newDaysList[k] = newDaysList[k].filter(
          d => !matchesTargetDog(d)
        );
        if (before !== newDaysList[k].length) {
          daysAffected++;

          // ✅ Vacío intencionado por borrado: marcar ANTES de setDaysList
          // (síncrono, sin await de por medio) para que
          // handleDaySyncFromCloud (AppCore.jsx) pueda aceptar el snapshot
          // vacío que Firestore emitirá tras la escritura de este borrado,
          // en vez de conservar el estado local por la protección
          // anti-pérdida general.
          if (newDaysList[k].length === 0) {
            markDeletedAll(selectedUser, dayKey);
          }
        }
      }
    });



    // 🔥 LIMPIAR ESTADOS ZOMBIE: Si se borra un perro en todos los días, limpiar
    // tanto la clave legacy por nombre como la clave estable por id (si se
    // conoce), para no dejar estados zombie bajo ninguna de las dos.
    targetDayKeys.forEach(dayKey => {
      delete newCompletedDogs[`${dayKey}-${normalizedDogName}`];
      delete newUrgentDogs[`${dayKey}-${normalizedDogName}`];
      if (dogId) {
        delete newCompletedDogs[`${dayKey}-${dogId}`];
        delete newUrgentDogs[`${dayKey}-${dogId}`];
      }
    });

    // ✅ GUARDAR EN STATE UNA VEZ (UI inmediata)
    setDaysList(newDaysList);
    setCompletedDogs(newCompletedDogs);
    setUrgentDogs(newUrgentDogs);
    recordSnapshotFunc(newDaysList, newCompletedDogs, newUrgentDogs);

    // 🔥 PERSISTIR ESTADOS LIMPIOS A FIRESTORE para los 27 días target
    if (workspaceId) {
      queueMicrotask(async () => {
        for (const dayKey of targetDayKeys) {
          try {
            const cleanedCompleted = Object.fromEntries(
              Object.entries(newCompletedDogs).filter(([k]) => k.startsWith(`${dayKey}-`))
            );
            const cleanedUrgent = Object.fromEntries(
              Object.entries(newUrgentDogs).filter(([k]) => k.startsWith(`${dayKey}-`))
            );
            await saveStates(workspaceId, dayKey, cleanedCompleted, cleanedUrgent, selectedUser);
          } catch (e) {
            console.error('[DEL_STATES_ERR]', dayKey, e?.message);
          }
        }
      });
    }

    // 🔥 PERSISTENCIA FIRESTORE: for...of await DIRECTO (secuencial, sin IIFE)
    let writesSucceeded = 0;
    let writesFailed = 0;
    
    if (!workspaceId) {
      // ❌ Sin workspaceId: error, no persistir
      toast({
        title: "Error",
        description: "No se puede eliminar en Firestore: workspaceId no disponible.",
      });
      
      console.error("[DEL_ERR_NO_WORKSPACE]", JSON.stringify({
        dogName: normalizedDogName,
        selectedUser,
        timestamp: new Date().toISOString()
      }));
    } else {
      // ✅ Con workspaceId: ejecutar for...of await
      for (const dayKey of targetDayKeys) {
        try {
          const ref = doc(db, 'workspaces', workspaceId, 'days', dayKey, 'profiles', selectedUser);
          const snap = await getDoc(ref);
          const dogsRemote = snap.data()?.dogs || [];
          const beforeLen = dogsRemote.length;
          
          // Fase 2: filtrar por identidad estable (id si se conoce, con
          // fallback legacy por nombre normalizado) — consistente con el
          // filtrado ya aplicado en memoria (matchesTargetDog). Sin esto,
          // dos clientes distintos con el mismo nombre visible pero id
          // distinto podían borrarse mutuamente en Firestore aunque en
          // memoria el borrado ya discriminara correctamente por id.
          const nextDogs = dogsRemote.filter(d => !matchesTargetDog(d));

          
          // Solo persistir si hubo cambio
          if (nextDogs.length < beforeLen) {
            await saveDogs(workspaceId, dayKey, nextDogs, selectedUser);
            writesSucceeded++;
            
            console.error("[DEL_DAY_WRITE]", JSON.stringify({
              dayKey,
              beforeLen,
              afterLen: nextDogs.length,
              path: ref.path,
              success: true,
              timestamp: new Date().toISOString()
            }));
          }
        } catch (err) {
          writesFailed++;
          console.error("[DEL_ERR]", JSON.stringify({
            dayKey,
            dogName: normalizedDogName,
            error: err?.message || String(err),
            willContinue: true,
            timestamp: new Date().toISOString()
          }));
          // Continuar con siguiente día (best-effort)
        }
      }

      // Marcar como eliminado permanentemente (normalizado para evitar duplicados)
      let permanentlyDeletedDogsRaw = loadFromStorage(
        LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS,
        [],
        selectedUser
      );
      let permanentlyDeletedDogs = Array.isArray(permanentlyDeletedDogsRaw)
        ? permanentlyDeletedDogsRaw
        : [];

      // Guardar y comparar con normalización
      if (!permanentlyDeletedDogs.includes(normalizedDogName)) {
        permanentlyDeletedDogs.push(normalizedDogName);
        saveToStorage(
          LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS,
          permanentlyDeletedDogs,
          selectedUser
        );
      }

      console.error("[DEL_DONE]", JSON.stringify({
        dogName: normalizedDogName,
        daysAffected,
        writesSucceeded,
        writesFailed,
        targetsCount: targetDayKeys.length,
        permanentlyDeleted: true,
        timestamp: new Date().toISOString()
      }));

      // 🔥 Eliminar también de la plantilla para que extendTemplateWindow no lo recree
      // (por identidad: id si se conoce, con fallback legacy por nombre normalizado)
      if (workspaceId) {
        queueMicrotask(() => {
          removeFromTemplate(workspaceId, selectedUser, normalizedDogName, dogId)
            .catch(e => console.error('[DEL_TEMPLATE_ERR]', e?.message));
        });
      }

    }
  }
};

export const handleEditDogInList = (
  oldName,
  newName,
  newWhatsApp,
  newCount,
  presenceDateKeys,
  isFixed,
  notesByDay,
  locationUrl,
  daysList,
  completedDogs,
  urgentDogs,
  setDaysList,
  setCompletedDogs,
  setUrgentDogs,
  recordSnapshotFunc,
  toast,
  getInitialDayData,
  currentSelectedDayKey,
  selectedUser,
  onlyThisWeek = true,
  schedules = [],
  // Alcance independiente por dateKey para notas informativas.
  // notesScopeByDay[dateKey]: true = puntual (solo esta semana), false = recurrente (todas las semanas)
  notesScopeByDay = {},
  // DateKeys cuya recurrencia debe terminar explícitamente desde changeDateKey
  terminatedNoteDays = [],
  // Fase 2: identidad estable (dog.id) del cliente editado, pasada desde la
  // UI (EditDogForm → useDogDayManager). Si se provee, tiene prioridad sobre
  // la búsqueda por nombre para resolver editedDogId; nunca se genera aquí.
  editedDogIdParam,
  // --- Coordinación edición/autoguardado/snapshots (weekOfChange) ---
  // Refs compartidos con AppCore (misma instancia, enhebrados vía
  // useDogDayManager.js). Si no se proveen (llamadas legacy/tests), toda
  // la coordinación de locks queda deshabilitada y el comportamiento es
  // exactamente el anterior (transacción directa sin cola ni locks).
  editLocksRef,
  editRevisionCounterRef,
  daysListRef,
  lastSavedDaysListRef,
  workspaceId
) => {

  const finalDogName = (newName || oldName || "").toUpperCase();

  const oldNameNormalized = (oldName || "").toUpperCase();

  const isRename = oldNameNormalized !== "" && oldNameNormalized !== finalDogName;
  // searchName: nombre usado para LOCALIZAR el registro existente.
  // En un renombrado hay que buscar por el nombre ANTIGUO (los datos siguen
  // guardados con ese nombre) pero escribir con el nombre NUEVO
  // (generalDogDataUpdate.name = finalDogName). Si no es un renombrado,
  // buscar y escribir coinciden (comportamiento original).
  const searchName = isRename ? oldNameNormalized : finalDogName;
  const presenceDateKeysSet = new Set(presenceDateKeys || []);

  // Fase 2 (bugfix duplicados/listas vacías tras rename+reorder casi seguidos):
  // Resolver editedDogId ANTES de construir cualquier lista, para poder
  // localizar el registro de la semana actual por identidad estable (id)
  // en lugar de solo por nombre. Se busca en `daysList` (snapshot recibido
  // como argumento) — exactamente la misma fuente que se usaba antes para
  // esta resolución, solo que ahora se calcula más pronto.
  let editedDogId = editedDogIdParam || null;
  let editedDogImportKey = null;
  // Historial de notas recurrentes ya existente para este perro (si lo hay).
  // PRIMERO: buscar en la semana de referencia que se está editando.
  // La semana de referencia es presenceDateKeys[0] o currentSelectedDayKey.
  // Esto asegura que el historial sea el VIGENTE de la edición, no una copia stale.
  // Si no existe en la semana de referencia, fallback a búsqueda legacy en otros días.
  
  const referenceDateKey = presenceDateKeys?.[0] || currentSelectedDayKey;
  const referenceKey = `${selectedUser}::${referenceDateKey}`;
  
  let existingRecurringNotes = [];
  let foundInReferenceWeek = false;
  
  // Fase 1: PRIMERO intentar obtener recurringNotes de la semana de referencia
  const referenceArr = daysList?.[referenceKey];
  if (Array.isArray(referenceArr)) {
    // Preferir búsqueda por editedDogIdParam si se provee
    let foundInRef = null;
    if (editedDogIdParam) {
      foundInRef = referenceArr.find((d) => d.id === editedDogIdParam);
    }
    // Fallback: búsqueda por nombre (legacy)
    if (!foundInRef) {
      foundInRef = referenceArr.find((d) => d.name === searchName);
    }
    
    if (foundInRef) {
      foundInReferenceWeek = true;
      if (!editedDogId && foundInRef.id) editedDogId = foundInRef.id;
      if (foundInRef.importKey) editedDogImportKey = foundInRef.importKey;
      // IMPORTANTE: usar recurringNotes INCLUSO SI ESTÁ VACÍO []
      // [] significa "sin recurrencia activa", NO "recurringNotes no existe"
      if (Array.isArray(foundInRef.recurringNotes)) {
        existingRecurringNotes = foundInRef.recurringNotes;
      }
    }
  }
  
  // Fase 2: FALLBACK legacy si no se encontró en la semana de referencia
  // Solo para resolver editedDogId/editedDogImportKey si falta
  if (!foundInReferenceWeek) {
    for (const k of Object.keys(daysList || {})) {
      if (!k.startsWith(`${selectedUser}::`)) continue;
      const arr = daysList[k];
      if (!Array.isArray(arr)) continue;
      const found = arr.find((d) => d.name === searchName);
      if (found) {
        if (!editedDogId && found.id) editedDogId = found.id;
        if (found.importKey) editedDogImportKey = found.importKey;
        // En fallback: solo usar recurringNotes si no se encontró en la semana de referencia
        // y aún no tenemos un historial válido
        if (existingRecurringNotes.length === 0 && Array.isArray(found.recurringNotes) && found.recurringNotes.length > 0) {
          existingRecurringNotes = found.recurringNotes;
        }
        if (editedDogId && editedDogImportKey && existingRecurringNotes.length > 0) break;
      }
    }
  }

  // --- Notas recurrentes: construir grupos texto→días desde notesByDay ---
  // Agrupa las fechas de la semana visible (weekOfChange, calculado más abajo
  // pero aquí solo necesitamos convertir cada dateKey a weekday) por texto NO
  // vacío. Días sin texto no forman parte de ningún grupo nuevo. Si dos días
  // comparten el mismo texto, forman un único grupo con ambos weekdays.
  const buildRuleGroupsFromNotesByDay = (notesByDayObj, weekDateKeys, scopeByDay, affectedWdays) => {
    const textToDays = new Map();
    (weekDateKeys || []).forEach((dateKey) => {
      // Solo considerar días con scope recurrente
      const scope = scopeByDay[dateKey] ?? true;
      if (scope === true) return;
      
      // Solo considerar weekdays afectados
      const weekday = getWeekdayFromDateKey(dateKey);
      if (affectedWdays && !affectedWdays.has(weekday)) return;
      
      const text = (notesByDayObj?.[dateKey] ?? "").trim();
      if (!text) return;
      
      if (!textToDays.has(text)) textToDays.set(text, []);
      textToDays.get(text).push(weekday);
    });
    return Array.from(textToDays.entries()).map(([text, days]) => ({ text, days }));
  };

  // weekOfChangeForNotes: mismo cálculo que weekOfChange (más abajo), pero
  // se necesita ya aquí para poder construir updatedRecurringNotes antes de
  // usarlo en el updater de setDaysList. Se recalcula weekOfChange después
  // con el mismo resultado (misma entrada), sin efectos secundarios.
  const weekOfChangeForNotes = getWeekDays(
    parseDateKey(presenceDateKeys?.[0] || currentSelectedDayKey)
  ).map(formatDateKey);

  // Calcular affectedWeekdays: weekdays cuya recurrencia realmente cambia en esta edición
  const terminatedWeekdays = new Set(
    (terminatedNoteDays || []).map(dk => getWeekdayFromDateKey(dk))
  );
  
  const affectedWeekdays = new Set();
  weekOfChangeForNotes.forEach((dateKey) => {
    const weekday = getWeekdayFromDateKey(dateKey);
    
    // Si esta dateKey debe terminar su recurrencia explícitamente
    if (terminatedWeekdays.has(weekday)) {
      affectedWeekdays.add(weekday);
      return;
    }
    
    // Si el scope es puntual, no afecta recurrencia
    const scope = notesScopeByDay[dateKey] ?? true;
    if (scope === true) return;
    
    // Scope recurrente: comparar si realmente cambió el texto
    const newText = (notesByDay[dateKey] ?? '').trim();
    const prevText = resolveNoteForDate(existingRecurringNotes, dateKey);
    if (newText !== prevText) {
      affectedWeekdays.add(weekday);
    }
  });

  const newRuleGroupsForNotes = buildRuleGroupsFromNotesByDay(notesByDay, weekOfChangeForNotes, notesScopeByDay, affectedWeekdays);
  const updatedRecurringNotes = reviseRecurringNotes(existingRecurringNotes, weekOfChangeForNotes[0], newRuleGroupsForNotes, affectedWeekdays);

  // Helper: campos de nota (informativeNote + informativeNoteOverride) para
  // una fecha concreta de la semana visible, según el alcance individual por dateKey
  const computeNoteFieldsForDate = (dateKey) => {
    const dayScope = notesScopeByDay[dateKey] ?? true;
    
    if (dayScope === true) {
      // Scope puntual: usar la nota puntual si existe
      const hasPunctualText = Object.prototype.hasOwnProperty.call(notesByDay || {}, dateKey);
      if (hasPunctualText) {
        return {
          informativeNote: notesByDay[dateKey],
          informativeNoteOverride: true,
        };
      }
      // Sin texto puntual: resolver desde recurrencia
      return {
        informativeNote: resolveNoteForDate(updatedRecurringNotes, dateKey),
        informativeNoteOverride: false,
      };
    }
    
    // Scope recurrente: siempre resolver desde updatedRecurringNotes
    return {
      informativeNote: resolveNoteForDate(updatedRecurringNotes, dateKey),
      informativeNoteOverride: false,
    };
  };

  // Matcher de identidad estable para localizar el registro del perro


  // editado: por `id` si se conoce (prioridad), con fallback legacy por
  // nombre normalizado para registros antiguos sin `id`. Se usa tanto para
  // la actualización LOCAL de la semana actual como para la persistencia
  // transaccional en Firestore de esa misma semana.
  const matchesEditedDog = (d) => {
    if (editedDogId && d?.id) return d.id === editedDogId;
    return d?.name === searchName;
  };


  // Separar schedules recurrentes de puntuales.
  // Solo los recurrentes (onlyThisWeek !== true) se propagan a semanas futuras.
  // Los puntuales (onlyThisWeek === true) solo se guardan en la semana actual.
  const allSchedules = Array.isArray(schedules) ? schedules : [];
  const recurringSchedules = allSchedules.filter(s => !(s.onlyThisWeek ?? false));

  const generalDogDataUpdate = {
    name: finalDogName,
    whatsappGroup: newWhatsApp || null,
    locationUrl: locationUrl || null,
    count:
      isNaN(parseInt(newCount, 10)) || parseInt(newCount, 10) < 1
        ? 1
        : parseInt(newCount, 10),
    isFixed: Boolean(isFixed),
    // Semana actual: todos los schedules (recurrentes + puntuales de esta semana)
    schedules: allSchedules,
    // Historial de notas recurrentes (dato de identidad, no "de esta
    // semana"): se guarda igual en semana actual y semanas futuras para que
    // resolveNoteForDate pueda recalcular la nota en cualquier día futuro
    // ya cargado que consulte este registro.
    recurringNotes: updatedRecurringNotes,
  };

  // Para propagación futura: solo schedules recurrentes (idéntico
  // recurringNotes, ya que es dato de identidad compartido)
  const generalDogDataUpdateFuture = {
    ...generalDogDataUpdate,
    schedules: recurringSchedules,
  };

  const permanentlyDeletedDogs =
    loadFromStorage(LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS, [], selectedUser) || [];

  const weekOfChange = getWeekDays(
    parseDateKey(presenceDateKeys?.[0] || currentSelectedDayKey)
  ).map(formatDateKey);

  // affectedWeekDateKeys: subconjunto de weekOfChange que este perro
  // REALMENTE afecta — presencia esperada (alta) o ya existía localmente
  // en `daysList` (snapshot previo a esta edición, para cubrir una baja).
  // Se calcula UNA sola vez, ANTES de tocar el estado local (setDaysList),
  // usando exactamente `daysList` (el argumento recibido, no prevDaysList
  // de ningún updater). El MISMO array se usa después tanto para crear
  // los locks de coordinación como para lanzar las transacciones de
  // persistencia: locks y transacciones cubren siempre el mismo conjunto
  // de fechas, nunca 7 fechas fijas.
  const affectedWeekDateKeys = weekOfChange.filter((dateKey) => {
    if (presenceDateKeysSet.has(dateKey)) return true;
    const ck = `${selectedUser}::${dateKey}`;
    const existingArr = daysList?.[ck] ?? daysList?.[dateKey];
    return Array.isArray(existingArr) && existingArr.some(matchesEditedDog);
  });

  const firstDateInPresence =

    presenceDateKeys?.length > 0
      ? parseDateKey(
          presenceDateKeys
            .slice()
            .sort(
              (a, b) => parseDateKey(a).getTime() - parseDateKey(b).getTime()
            )[0]
        )
      : parseDateKey(currentSelectedDayKey);

  const presenceDayNamesForFields = new Set(
    (presenceDateKeys || []).map(k => getDayNameFromDateKey(k))
  );
  // Mismo conjunto de días de la semana presentes, reutilizado en la rama
  // !onlyThisWeek (antes se recalculaba con el mismo contenido dos veces).
  const presenceDayNames = presenceDayNamesForFields;

  // renamedDogId: identidad estable para localizar el registro en la
  // plantilla al renombrar. editedDogId ya fue resuelto arriba buscando en
  // TODOS los días cargados del perfil (superconjunto de weekOfChange), así
  // que no hace falta repetir la búsqueda aquí.
  const renamedDogId = editedDogId || null;

  // 🔄 MIGRAR ESTADOS (completado/urgente) del nombre antiguo al nuevo.
  // Se calcula ANTES de setDaysList porque solo depende de completedDogs/
  // urgentDogs (args de closure) + isRename/searchName/finalDogName/
  // editedDogId — nada de esto depende del nuevo daysList, así que no hace
  // falta esperar a que el updater funcional se resuelva.
  //
  // Las claves de completedDogs/urgentDogs tienen forma `${dayKey}-${identidad}`.
  // Fase 2: la clave DESTINO estable es `${dayKey}-${editedDogId}` si el perro
  // ya tiene identidad (id); si es un registro legacy sin id, la clave destino
  // sigue siendo `${dayKey}-${nombreNuevoNormalizado}` (comportamiento anterior).
  // Se migran AMBAS claves legacy posibles — `${dayKey}-${oldName}` y
  // `${dayKey}-${newName}` — hacia la clave destino, sin perder ningún valor
  // true ya existente (una clave legacy con true nunca es sobrescrita por
  // una ausente/false de la otra).
  let finalCompletedDogs = completedDogs;
  let finalUrgentDogs = urgentDogs;
  const affectedStateDayKeys = new Set();

  if (isRename) {
    const oldSuffix = `-${searchName}`;
    const newSuffix = `-${finalDogName}`;
    const migrateStateKeys = (stateObj) => {
      const migrated = { ...stateObj };
      Object.keys(stateObj).forEach((key) => {
        const isOldKey = key.endsWith(oldSuffix);
        const isNewLegacyKey = !isOldKey && key.endsWith(newSuffix);
        if (!isOldKey && !isNewLegacyKey) return;

        const suffixLen = isOldKey ? oldSuffix.length : newSuffix.length;
        const dayKeyPart = key.slice(0, key.length - suffixLen);
        // Clave destino estable: por id si existe, si no por nombre nuevo
        // normalizado (fallback legacy, igual que el comportamiento previo).
        const destKey = editedDogId
          ? `${dayKeyPart}-${editedDogId}`
          : `${dayKeyPart}-${finalDogName}`;

        // No perder un valor true ya presente en destino por culpa de un
        // valor false/ausente de la otra clave legacy.
        if (stateObj[key] || !migrated[destKey]) {
          migrated[destKey] = stateObj[key];
        }
        if (key !== destKey) delete migrated[key];
        affectedStateDayKeys.add(dayKeyPart);
      });
      return migrated;
    };
    finalCompletedDogs = migrateStateKeys(completedDogs);
    finalUrgentDogs = migrateStateKeys(urgentDogs);
  }

  // Bugfix duplicados/listas vacías (rename + reorder casi seguidos):
  // TODA la construcción del nuevo daysList local (semana actual + rename
  // futuro + campos futuros + propagación completa) ocurre dentro de un
  // ÚNICO updater funcional de setDaysList, clonando siempre `prevDaysList`
  // (estado más reciente en el momento del commit de React) en vez del
  // `daysList` capturado por closure en el momento de la llamada. Sin esto,
  // dos ediciones/reorders disparados casi a la vez podían pisarse: la
  // segunda sobrescribía con una copia desactualizada de la primera.
  // Dentro del updater: solo clonado + cálculo síncrono. Sin await, sin
  // llamadas a Firebase, sin mutar `prevDaysList`.
  const propagatedKeysForPersist = new Set();
  let capturedNewDaysList = null;

  // --- Coordinación edición/autoguardado/snapshots (weekOfChange) ---
  // Crear, SÍNCRONAMENTE, un lock por cada dateKey de weekOfChange ANTES
  // del setDaysList optimista y antes de lanzar cualquier trabajo async.
  // myEditRevision identifica esta operación de edición de forma única;
  // se usa para revalidar (en la transacción y en handleDaySyncFromCloud)
  // que un lock no fue reemplazado por una edición posterior sobre el
  // mismo documento. Si no se proveen los refs (llamadas legacy/tests),
  // esta coordinación queda deshabilitada sin alterar el resto del flujo.
  const myEditRevision = editRevisionCounterRef ? (++editRevisionCounterRef.current) : null;
  const _workspaceIdForLocks = editLocksRef ? workspaceId : null;
  // Locks creados SOLO para affectedWeekDateKeys (no los 7 días fijos de
  // weekOfChange): así una edición que no afecta a un dateKey concreto de
  // la semana visible (el perro ni debía estar presente ni existía ya ahí)
  // nunca bloquea el autoguardado de ese día para otros clientes.
  if (editLocksRef && _workspaceIdForLocks) {
    affectedWeekDateKeys.forEach((dateKey) => {
      const lockKeyForDate = makeEditLockKey(_workspaceIdForLocks, selectedUser, dateKey);
      const expectedPresenceForLock = presenceDateKeysSet.has(dateKey);
      editLocksRef.current.set(lockKeyForDate, {
        revision: myEditRevision,
        dogMatch: {
          id: editedDogId || null,
          oldName: searchName,
          newName: finalDogName,
        },
        expectedPresence: expectedPresenceForLock,
        // count incluido en expectedFields (además de las notas) para que
        // la reconciliación en AppCore.jsx pueda validar también el campo
        // count del registro remoto, no solo informativeNote/override.
        expectedFields: expectedPresenceForLock
          ? { ...computeNoteFieldsForDate(dateKey), count: generalDogDataUpdate.count }
          : null,
        status: 'pending',
        rawConfirmed: false,
        rawConfirmedRevision: null,
        committedRecord: null,
      });
    });
  }


  setDaysList((prevDaysList) => {

    const newDaysList = deepClone(prevDaysList || {});

    // --- Semana actual (weekOfChange): localizar por identidad estable ---
    weekOfChange.forEach((dateKey) => {
      const ck = `${selectedUser}::${dateKey}`;
      if (!newDaysList[ck]) {
        newDaysList[ck] = newDaysList[dateKey]
          ? [...newDaysList[dateKey]]
          : getInitialDayData(
              dateKey,
              selectedUser,
              permanentlyDeletedDogs
            );
      }
      // Localizar por identidad estable (id primero, fallback legacy por
      // nombre) en vez de solo por nombre: así un id ya existente nunca
      // provoca un push de fila duplicada aunque el nombre visible cambie.
      const dogIndex = newDaysList[ck].findIndex(matchesEditedDog);
      const shouldBePresent = presenceDateKeysSet.has(dateKey);

      if (dogIndex !== -1) {
        if (shouldBePresent) {
          const noteFieldsLocal = computeNoteFieldsForDate(dateKey);
          if (finalDogName === 'POMPON' || finalDogName === 'MUSHU' || finalDogName === 'PIPPO' || finalDogName === 'MURA' || finalDogName === 'RIO' || finalDogName === 'CLAY' || finalDogName === 'MOWGLY' || finalDogName === 'LARA') {
            console.log('[NOTE_TRACE]', JSON.stringify({
              source: 'LOCAL_SET',


              dogId: editedDogId || null,
              name: finalDogName,
              dayKey: dateKey,
              note: noteFieldsLocal.informativeNote,
              override: noteFieldsLocal.informativeNoteOverride,
              ts: Date.now(),
            }));
          }
          newDaysList[ck][dogIndex] = {
            ...newDaysList[ck][dogIndex],
            ...generalDogDataUpdate,
            ...noteFieldsLocal,
          };
        } else {
          newDaysList[ck].splice(dogIndex, 1);
        }
      } else if (shouldBePresent) {

        // No existe todavía por identidad en este día: alta de presencia
        // nueva (no duplicar). getInitialDayData siempre devuelve [] (no hay
        // semillas en runtime), así que jamás debe usarse Date.now()/
        // Math.random() como fallback de originalOrder: se calcula un orden
        // ENTERO DETERMINISTA (máximo originalOrder ya presente en este día + 1).
        if (isRename) {
          console.warn('[EDIT_DOG_MISSING_ON_RENAME]', JSON.stringify({
            profile: selectedUser,
            dateKey,
            searchName,
            finalDogName,
            editedDogId: editedDogId || null,
          }));
        }
        const nextOrderLocal = (newDaysList[ck] || []).reduce((max, d) => {
          const o = typeof d?.originalOrder === "number" ? d.originalOrder : -Infinity;
          return o > max ? o : max;
        }, -1) + 1;
        newDaysList[ck].push({
          ...generalDogDataUpdate,
          ...computeNoteFieldsForDate(dateKey),
          originalOrder: nextOrderLocal,
          firstAddedDateKey: presenceDateKeys?.[0] || currentSelectedDayKey,
          ...(editedDogId ? { id: editedDogId } : {}),
        });
      }

    });

    // --- Renombrado: propagar el NOMBRE a semanas futuras ya cargadas ---
    if (isRename) {
      Object.keys(newDaysList).forEach((k) => {
        if (!k.startsWith(`${selectedUser}::`)) return;
        const dayKeyPart = k.split('::')[1];
        if (weekOfChange.includes(dayKeyPart)) return; // semana actual ya tratada arriba
        const arr = newDaysList[k];
        if (!Array.isArray(arr)) return;
        const dogIdx = arr.findIndex(matchesEditedDog);
        if (dogIdx === -1) return;
        newDaysList[k] = arr.map((d, i) =>
          i === dogIdx ? { ...d, name: finalDogName } : d
        );
      });
    }

    if (onlyThisWeek && affectedWeekdays.size > 0) {
    // Propagar updatedRecurringNotes a semanas futuras ya cargadas
    Object.keys(newDaysList).forEach((k) => {
      if (!k.startsWith(`${selectedUser}::`)) return;
      const dayKey = k.split('::')[1];
      if (weekOfChange.includes(dayKey)) return; // semana actual ya tratada
      if (!presenceDayNamesForFields.has(getDayNameFromDateKey(dayKey))) return;
      const arr = newDaysList[k];
      if (!Array.isArray(arr)) return;
      const dogIdx = arr.findIndex(matchesEditedDog);
      if (dogIdx === -1) return;
      // Actualizar SOLO recurringNotes y la informativeNote derivada en semanas futuras cargadas
      const futureEntry = arr[dogIdx];
      const noteFields = isProtectedNoteException(futureEntry)
        ? {} // Excepción puntual protegida: NO sobrescribir
        : {
            informativeNote: resolveNoteForDate(updatedRecurringNotes, dayKey),
            informativeNoteOverride: false,
          };
      newDaysList[k] = arr.map((d, i) =>
        i === dogIdx
          ? { ...d, recurringNotes: updatedRecurringNotes, ...noteFields }
          : d
      );
    });
    }

    // --- onlyThisWeek: propagar whatsapp/localización/schedules recurrentes ---
    if (onlyThisWeek) {
      Object.keys(newDaysList).forEach((k) => {
        if (!k.startsWith(`${selectedUser}::`)) return;
        const dayKey = k.split('::')[1];
        if (weekOfChange.includes(dayKey)) return; // semana actual ya tratada
        if (!presenceDayNamesForFields.has(getDayNameFromDateKey(dayKey))) return;
        const arr = newDaysList[k];
        if (!Array.isArray(arr)) return;
        const dogIdx = arr.findIndex(matchesEditedDog);
        if (dogIdx === -1) return;
        newDaysList[k] = arr.map((d, i) =>
          i === dogIdx
            ? { ...d, whatsappGroup: newWhatsApp || null, locationUrl: locationUrl || null, schedules: recurringSchedules }
            : d
        );
      });
    }

    // --- !onlyThisWeek: propagación completa a semanas futuras ---
    if (!onlyThisWeek) {
      for (let i = 1; i < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; i++) {
        const weekStartDate = new Date(firstDateInPresence);
        weekStartDate.setDate(weekStartDate.getDate() + i * 7);

        getWeekDays(weekStartDate).forEach((currentDayDate) => {
          const targetDateKey = formatDateKey(currentDayDate);
          const ck = `${selectedUser}::${targetDateKey}`;
          const dayName = getDayNameFromDateKey(targetDateKey);
          const shouldBePresent = presenceDayNames.has(dayName);

          // 🔒 GUARD ANTI-CORRUPCIÓN: comprobar si esta semana futura YA
          // estaba cargada en memoria, usando el estado FRESCO (prevDaysList
          // recibido por el updater) en vez del `daysList` capturado por
          // closure en el momento de la llamada. Si NO estaba cargada,
          // getInitialDayData() devolvería [] y cualquier push/splice
          // dejaría localmente una lista incompleta, que el autoguardado de
          // AppCore.jsx podría acabar persistiendo, sobrescribiendo una
          // lista completa real. En ese caso: NO tocar newDaysList[ck] en
          // absoluto; solo marcar propagatedKeysForPersist para que la
          // persistencia Firestore de abajo haga read-before-write.
          const wasLoadedLocally =
            Array.isArray(prevDaysList?.[ck]) || Array.isArray(prevDaysList?.[targetDateKey]);

          if (!wasLoadedLocally) {
            propagatedKeysForPersist.add(targetDateKey);
            return;
          }

          if (!newDaysList[ck]) {
            newDaysList[ck] = getInitialDayData(
              targetDateKey,
              selectedUser,
              permanentlyDeletedDogs
            );
          }

          // 🔒 GUARD ANTI-CORRUPCIÓN (lista parcial): un array local ya
          // existente pero vacío es indistinguible entre "Firestore confirmó
          // que este día no tiene perros" y "día virgen aún no materializado
          // desde plantilla" (por ejemplo, tras visitar directamente esa
          // fecha desde el calendario, que aplica cloud=[] localmente). En
          // ambos casos NO fabricar aquí una entrada parcial con solo el
          // cliente editado: diferir a la transacción Firestore de abajo
          // (que hace read-before-write real) exactamente igual que el caso
          // "no cargado localmente".
          if (newDaysList[ck].length === 0) {
            propagatedKeysForPersist.add(targetDateKey);
            return;
          }

          const dogIndex = newDaysList[ck].findIndex(matchesEditedDog);


          if (dogIndex !== -1) {
            if (shouldBePresent) {
              // Semanas futuras: solo schedules recurrentes. Nota: si el
              // registro futuro tiene informativeNoteOverride === true
              // (excepción puntual protegida), NO se recalcula su nota; en
              // otro caso se recalcula desde updatedRecurringNotes.
              const futureEntry = newDaysList[ck][dogIndex];
              const noteFields = isProtectedNoteException(futureEntry)
                ? {}
                : {
                    informativeNote: resolveNoteForDate(updatedRecurringNotes, targetDateKey),
                    informativeNoteOverride: false,
                  };
              newDaysList[ck][dogIndex] = {
                ...futureEntry,
                ...generalDogDataUpdateFuture,
                ...noteFields,
              };
              propagatedKeysForPersist.add(targetDateKey);
            } else {
              newDaysList[ck].splice(dogIndex, 1);
              propagatedKeysForPersist.add(targetDateKey);
            }
          } else if (shouldBePresent) {
            // getInitialDayData siempre devuelve [] (sin semillas en
            // runtime): jamas usar Date.now()/Math.random() como fallback
            // de originalOrder aqui. Orden ENTERO DETERMINISTA: maximo
            // originalOrder ya presente en este dia futuro + 1.
            if (isRename) {
              console.warn('[EDIT_DOG_MISSING_ON_RENAME]', JSON.stringify({
                profile: selectedUser,
                dateKey: targetDateKey,
                searchName,
                finalDogName,
                editedDogId: editedDogId || null,
              }));
            }
            const nextOrderFuture = (newDaysList[ck] || []).reduce((max, d) => {
              const o = typeof d?.originalOrder === "number" ? d.originalOrder : -Infinity;
              return o > max ? o : max;
            }, -1) + 1;
            // Semanas futuras: solo schedules recurrentes
            newDaysList[ck].push({
              ...generalDogDataUpdateFuture,
              informativeNote: "",
              originalOrder: nextOrderFuture,
              firstAddedDateKey: presenceDateKeys?.[0] || currentSelectedDayKey,
              ...(editedDogId ? { id: editedDogId } : {}),
            });
            propagatedKeysForPersist.add(targetDateKey);
          } else {
            // Perro no cargado en memoria local pero puede existir en
            // Firestore (semana futura no cargada). Añadir para que el
            // bloque de persistencia haga read-before-write y lo elimine.
            propagatedKeysForPersist.add(targetDateKey);
          }
        });
      }
    }
    // else: onlyThisWeek=true → no procesar semanas futuras (weekOfChange ya gestionó la semana actual)

    // --- Ordenar todas las listas modificadas ---
    Object.keys(newDaysList).forEach((dateKey) => {
      if (Array.isArray(newDaysList[dateKey])) {
        newDaysList[dateKey].sort(
          (a, b) => (a.originalOrder || 0) - (b.originalOrder || 0)
        );
      }
    });

    // Capturar la lista final resultante para poder usarla en recordSnapshot
    // (fuera del updater), ya que este es el único lugar donde `newDaysList`
    // (la versión más reciente, construida sobre prevDaysList) existe.
    capturedNewDaysList = newDaysList;
    return newDaysList;
  });

  // ------------------------------------------------------------------
  // 2) PERSISTENCIA FIRESTORE — fuera del updater, fire-and-forget.
  // ------------------------------------------------------------------

  // Matcher EXCLUSIVO para localizar, dentro de committedDogs (el array
  // que la transacción realmente escribió), el registro correspondiente al
  // cliente editado. Distinto de matchesEditedDog: en legacy acepta tanto
  // searchName (nombre antiguo) como finalDogName (nombre nuevo), porque
  // committedDogs ya refleja el patch aplicado (con el nombre nuevo) pero
  // el matcher debe seguir localizándolo aunque, por cualquier razón, algún
  // registro remoto conservara el nombre antiguo. No sustituye a
  // matchesEditedDog en el resto del código.
  const matchesCommittedRecord = (d) => {
    if (editedDogId && d?.id) return d.id === editedDogId;
    return d?.name === searchName || d?.name === finalDogName;
  };

  // Helper de recuperación (documento inexistente para escribir, commit
  // fallido manteniendo nuestra revisión, o expectedPresence=true sin
  // committedRecord localizado). Nunca deja un lock 'pending' silenciosamente:
  // lo marca 'failed', avisa al usuario, y hace UNA lectura getDocFromServer
  // (nunca caché) para resincronizar React/daysListRef/lastSavedDaysListRef
  // con el RAW real del servidor. Si esa lectura también falla, el lock
  // permanece 'failed' — sin fallback a saveDogs — hasta que una edición
  // posterior lo reemplace (nueva revisión) o un snapshot RAW posterior
  // active la rama 'failed' ya implementada en AppCore.jsx.
  const recoverFromServerOrKeepFailed = async (dateKey, docRef, lockKeyForDate, revisionForThisEdit) => {
    if (!editLocksRef) return;
    const currentLock = editLocksRef.current.get(lockKeyForDate);
    if (!currentLock || currentLock.revision !== revisionForThisEdit) {
      // El lock ya no es nuestro (reemplazado por una edición posterior):
      // nada que recuperar aquí, no tocar ese lock más nuevo.
      return;
    }
    currentLock.status = 'failed';

    toast({
      title: "Error al guardar",
      description: `No se pudo confirmar el cambio para ${dateKey}. Sincronizando con el servidor…`,
      variant: "destructive",
    });


    try {
      const serverSnap = await getDocFromServer(docRef);
      const serverDogs = serverSnap.exists() ? (serverSnap.data()?.dogs ?? []) : [];

      const lockAfterRead = editLocksRef.current.get(lockKeyForDate);
      if (!lockAfterRead || lockAfterRead.revision !== revisionForThisEdit) {
        return; // Reemplazado mientras leíamos: no pisar la edición nueva.
      }

      setDaysList((prevDaysList) => {
        const compositeKeyRecover = `${selectedUser}::${dateKey}`;
        const finalArr = serverDogs.map((d) => ({ ...d }));
        const nextDaysList = { ...prevDaysList, [compositeKeyRecover]: finalArr };
        if (daysListRef) daysListRef.current = nextDaysList;
        if (lastSavedDaysListRef?.current) {
          lastSavedDaysListRef.current[compositeKeyRecover] = finalArr;
        }
        const lockToClear = editLocksRef.current.get(lockKeyForDate);
        if (lockToClear && lockToClear.revision === revisionForThisEdit) {
          editLocksRef.current.delete(lockKeyForDate);
        }
        return nextDaysList;
      });
    } catch (e) {
      // La lectura del servidor también falló: mantener status='failed',
      // no ejecutar saveDogs, no afirmar limpieza automática.
      console.error('[EDIT_LOCK_RECOVER_ERR]', dateKey, e?.message);
    }
  };

  // Semana actual: runTransaction localizando el registro por identidad
  // estable (matchesEditedDog: id primero, fallback legacy por nombre).
  // Coordinación (weekOfChange): esta transacción se ejecuta encolada en
  // la MISMA cola FIFO por documento (enqueueDocWrite + makeEditLockKey)
  // que usa el autoguardado de AppCore.jsx, garantizando un único escritor
  // activo por documento. committedDogs/hadCommit se reinician al
  // principio de CADA intento/reintento de runTransaction: un reintento
  // descartado nunca puede dejar hadCommit=true de un intento anterior.
  // Resultado explícito por dateKey ({dateKey, ok, reason}) para poder
  // condicionar el toast de éxito a la confirmación real de TODAS las
  // fechas de affectedWeekDateKeys (nunca de weekOfChange completo, que
  // puede incluir fechas que este perro ni siquiera afecta). runTx ahora
  // PROPAGA (throw) en cualquier vía de recuperación/fallo, o cuando
  // detecta que su revisión fue sustituida por una edición posterior sobre
  // el mismo documento — en ese último caso nunca toca el lock (que ya
  // pertenece a la edición nueva), solo deja constancia del fallo para
  // ESTA operación.
  const _workspaceId = workspaceId;
  if (_workspaceId && affectedWeekDateKeys.length > 0) {
    queueMicrotask(async () => {
      const perDateResults = await Promise.all(affectedWeekDateKeys.map(async (dateKey) => {
        const docRef = doc(db, `workspaces/${_workspaceId}/days/${dateKey}/profiles/${selectedUser}`);
        const lockKeyForDate = makeEditLockKey(_workspaceId, selectedUser, dateKey);

        const runTx = async () => {
          let committedDogs = null;
          let hadCommit = false;
          // Indicador de "estado remoto ya satisfecho": documento
          // inexistente + expectedPresence=false. Distinto de un fallo:
          // no hay nada que escribir porque la ausencia YA es el estado
          // correcto. Se reinicia en cada intento/reintento igual que
          // committedDogs/hadCommit (ver reset obligatorio abajo).
          let docAbsentAndAlreadySatisfied = false;

          try {
            await runTransaction(db, async (tx) => {
              // Reset obligatorio en CADA intento: ver comentario arriba.
              committedDogs = null;
              hadCommit = false;
              docAbsentAndAlreadySatisfied = false;

              const docSnap = await tx.get(docRef);
              const docExists = docSnap.exists();
              const shouldBePresent = presenceDateKeysSet.has(dateKey);

              if (!docExists && !shouldBePresent) {
                // hadCommit permanece false. No se esperaba presencia en
                // este dateKey y el documento no existe: el estado
                // remoto YA está satisfecho (nada que escribir): no es
                // un error, se trata expresamente después (fuera de la
                // transacción).
                docAbsentAndAlreadySatisfied = true;
                return;
              }

              // Revalidar que el lock sigue existiendo y mantiene
              // myEditRevision. Si fue sustituido por una edición
              // posterior, return sin escribir. Se aplica también
              // cuando el documento no existe (creación), para no
              // crear un documento con una revisión ya obsoleta.
              if (editLocksRef) {
                const currentLock = editLocksRef.current.get(lockKeyForDate);
                if (!currentLock || currentLock.revision !== myEditRevision) {
                  return;
                }
              }

              // Documento ausente + presencia esperada: tratar como
              // remoto vacío y continuar por la MISMA lógica común de
              // abajo (crea el documento vía el mismo tx.set, sin una
              // segunda implementación independiente).
              const remoteExisting = docExists ? (docSnap.data()?.dogs ?? []) : [];
              const existingEntry = remoteExisting.find(matchesEditedDog);


              let dogsToWrite;
              if (shouldBePresent) {
                const patch = {
                  ...generalDogDataUpdate,
                  ...computeNoteFieldsForDate(dateKey),
                  originalOrder: existingEntry?.originalOrder ?? Date.now(),
                  firstAddedDateKey: existingEntry?.firstAddedDateKey ?? presenceDateKeys?.[0] ?? currentSelectedDayKey,
                };
                if (finalDogName === 'POMPON' || finalDogName === 'MUSHU' || finalDogName === 'PIPPO' || finalDogName === 'MURA' || finalDogName === 'RIO' || finalDogName === 'CLAY' || finalDogName === 'MOWGLY' || finalDogName === 'LARA') {
                  console.log('[NOTE_TRACE]', JSON.stringify({
                    source: 'TX_CURRENT_WEEK',


                    dogId: editedDogId || null,
                    name: finalDogName,
                    dayKey: dateKey,
                    existingEntryOverride: existingEntry?.informativeNoteOverride ?? null,
                    patchNote: patch.informativeNote,
                    patchOverride: patch.informativeNoteOverride,
                    ts: Date.now(),
                  }));
                }
                if (existingEntry) {

                  // Localizado por identidad: aplicar el patch EXACTAMENTE
                  // sobre ese registro, sin crear fila nueva aunque el nombre
                  // ya no coincida (rename en curso).
                  dogsToWrite = remoteExisting.map((d) =>
                    matchesEditedDog(d) ? { ...d, ...patch } : d
                  );
                } else {
                  // No existía todavía en remoto (semana recién materializada)
                  dogsToWrite = [
                    ...remoteExisting,
                    { ...patch, ...(editedDogId ? { id: editedDogId } : {}) },
                  ];
                }
              } else {
                dogsToWrite = remoteExisting.filter((d) => !matchesEditedDog(d));
              }

              const sanitizedDogs = sanitizeForFirestore(dogsToWrite);
              // Asignar committedDogs y hadCommit=true justo antes de tx.set.
              committedDogs = sanitizedDogs;
              hadCommit = true;
              tx.set(docRef, { dogs: sanitizedDogs, count: sanitizedDogs.length }, { merge: true });
            });
          } catch (e) {
            console.error('[EDIT_CURRENT_WEEK_ERR]', dateKey, e?.message);
            await recoverFromServerOrKeepFailed(dateKey, docRef, lockKeyForDate, myEditRevision);
            // Propagar: esta fecha NO quedó confirmada, el toast de éxito
            // no debe mostrarse.
            throw e;
          }

          // Sin coordinación de locks (refs no provistos, p.ej. llamadas
          // legacy/tests): comportamiento idéntico al anterior, sin pasos
          // adicionales de confirmación/lock. Se considera éxito (la
          // transacción ya confirmó la escritura en el servidor).
          if (!editLocksRef) return;

          if (!hadCommit) {
            if (docAbsentAndAlreadySatisfied) {
              // Documento inexistente + ausencia esperada: el estado
              // remoto YA está satisfecho. No es un error: no se llama a
              // recoverFromServerOrKeepFailed ni se muestra toast destructivo.
              const lockNowAbsent = editLocksRef.current.get(lockKeyForDate);
              if (lockNowAbsent && lockNowAbsent.revision === myEditRevision) {
                setDaysList((prevDaysList) => {
                  const compositeKeyForDate = `${selectedUser}::${dateKey}`;
                  const prevArr = Array.isArray(prevDaysList[compositeKeyForDate])
                    ? prevDaysList[compositeKeyForDate]
                    : [];
                  // Filtrar ÚNICAMENTE el cliente editado; conservar
                  // intactos y en el mismo orden todos los demás clientes
                  // locales. El documento remoto inexistente no implica
                  // que el día completo deba vaciarse.
                  const finalArr = prevArr.filter((d) => !matchesEditedDog(d));
                  const nextDaysList = { ...prevDaysList, [compositeKeyForDate]: finalArr };
                  if (daysListRef) daysListRef.current = nextDaysList;
                  // El estado RAW real del servidor es "documento
                  // inexistente" (equivalente a []): lastSavedDaysListRef
                  // debe registrar exactamente eso, no finalArr.
                  if (lastSavedDaysListRef?.current) {
                    lastSavedDaysListRef.current[compositeKeyForDate] = [];
                  }
                  const lockToClearAbsent = editLocksRef.current.get(lockKeyForDate);
                  if (lockToClearAbsent && lockToClearAbsent.revision === myEditRevision) {
                    editLocksRef.current.delete(lockKeyForDate);
                  }
                  return nextDaysList;
                });
              }
              // El autoguardado normal (misma cola, daysListRef fresco)
              // persistirá después la lista local completa si contiene
              // otros clientes; aquí no se llama a saveDogs. Éxito: el
              // estado remoto ya coincidía con lo esperado.
              return;
            }
            // Documento no existía con presencia esperada: recuperar y
            // propagar como fallo para esta fecha.
            await recoverFromServerOrKeepFailed(dateKey, docRef, lockKeyForDate, myEditRevision);
            throw new Error(`NO_COMMIT:${dateKey}`);
          }

           const shouldBePresent = presenceDateKeysSet.has(dateKey);
           // Localizar committedRecord con el matcher EXCLUSIVO para
           // committedDogs (id si ambos lo tienen; legacy: searchName o
           // finalDogName).
           const committedRecord = committedDogs.find(matchesCommittedRecord) || null;

            if (shouldBePresent && !committedRecord) {
              // Nunca crear un lock committed con presencia esperada y
              // committedRecord=null: tratar como fallo.
              await recoverFromServerOrKeepFailed(dateKey, docRef, lockKeyForDate, myEditRevision);
              throw new Error(`COMMITTED_RECORD_MISSING:${dateKey}`);
            }

          const lockNow = editLocksRef.current.get(lockKeyForDate);
          if (!lockNow || lockNow.revision !== myEditRevision) {
            // Reemplazado por una edición posterior: NUNCA tocar ese lock
            // (ya pertenece a la revisión nueva), pero esta operación
            // antigua debe propagarse como no-confirmada.
            throw new Error(`LOCK_SUPERSEDED:${dateKey}`);
          }

          if (lockNow.rawConfirmed === true && lockNow.rawConfirmedRevision === myEditRevision) {
            // Confirmación anticipada: el snapshot RAW ya confirmó el
            // contenido antes de que la transacción resolviera. Aplicar el
            // array COMPLETO committedDogs (nunca reconstruirlo desde el
            // array local anterior), en un único callback de setDaysList.
            setDaysList((prevDaysList) => {
              const compositeKeyForDate = `${selectedUser}::${dateKey}`;
              const finalArr = committedDogs.map((d) => ({ ...d }));
              const nextDaysList = { ...prevDaysList, [compositeKeyForDate]: finalArr };
              if (daysListRef) daysListRef.current = nextDaysList;
              if (lastSavedDaysListRef?.current) {
                lastSavedDaysListRef.current[compositeKeyForDate] = finalArr;
              }
              const lockToClear = editLocksRef.current.get(lockKeyForDate);
              if (lockToClear && lockToClear.revision === myEditRevision) {
                editLocksRef.current.delete(lockKeyForDate);
              }
              return nextDaysList;
            });
          } else {
            // Todavía no existe rawConfirmed: cambiar el lock a 'committed'
            // y guardar committedRecord. handleDaySyncFromCloud liberará
            // este lock cuando el próximo snapshot RAW confirme el
            // contenido exacto (informativeNote/informativeNoteOverride).
            lockNow.status = 'committed';
            lockNow.committedRecord = committedRecord ? { ...committedRecord } : null;
          }
          // Éxito: la transacción confirmó la escritura en el servidor
          // (hadCommit=true, presencia esperada satisfecha por
          // committedRecord si aplica, lock aún nuestro).
        };

        try {
          await enqueueDocWrite(lockKeyForDate, runTx);
          return { dateKey, ok: true };
        } catch (e) {
          console.error('[EDIT_CURRENT_WEEK_QUEUE_ERR]', dateKey, e?.message);
          return { dateKey, ok: false, reason: e?.message || String(e) };
        }
      }));

      // Toast de éxito SOLO si TODAS las fechas de affectedWeekDateKeys
      // (la semana visible realmente afectada, nunca las 26 semanas
      // futuras de propagación fire-and-forget) confirmaron la escritura
      // real en el servidor. Cualquier fallo, recuperación fallida o
      // revisión sustituida por una edición posterior evita el toast de
      // éxito (sin mostrar avisos duplicados; los errores ya se loguean
      // arriba y recoverFromServerOrKeepFailed ya mostró su propio toast
      // destructivo cuando aplicó).
      const allConfirmed =
        perDateResults.length === affectedWeekDateKeys.length &&
        perDateResults.every((r) => r.ok);

      if (allConfirmed) {
        toast({
          title: "Cliente actualizado",
          description: `Los datos de ${finalDogName} han sido actualizados.`,
        });
      }
    });
  }



  // 🔄 RENOMBRADO: propagar el cambio de NOMBRE a la plantilla SIEMPRE,
  // independientemente de onlyThisWeek. El nombre es un dato de identidad
  // del cliente (no un dato "de esta semana"): si no se propaga, el mismo
  // cliente quedaría con nombres distintos según la semana, rompiendo el
  // histórico de horarios/notas/WhatsApp/localización ligado al registro.
  if (isRename) {
    const workspaceIdRename = workspaceId;
    if (workspaceIdRename) {
      // 🔥 Renombrar también en la plantilla (template) para que
      // extendTemplateWindow no resucite el nombre antiguo en rutas futuras
      // aún no materializadas en Firestore. Esto es un documento distinto
      // (templates/{profile}), sin relación con la carrera de days/*.
      queueMicrotask(() => {
        renameDogInTemplate(
          workspaceIdRename,
          selectedUser,
          { id: renamedDogId || undefined, oldName: searchName },
          finalDogName
        ).catch((e) => console.error('[EDIT_RENAME_TEMPLATE_ERR]', e?.message));
      });
    }
  }

  // ✅ SIEMPRE propagar whatsappGroup y locationUrl a semanas futuras
  // independientemente de onlyThisWeek.
  // Cuando onlyThisWeek=false, el bloque de abajo ya lo hace todo;
  // cuando onlyThisWeek=true solo actualizamos estos dos campos específicos.
  if (onlyThisWeek) {
    // Fase 2 integridad (C): CONSOLIDACIÓN DE ESCRITURAS FUTURAS.
    // Antes había un bucle separado (getDoc + saveDogs) para el RENOMBRADO
    // y otro bucle separado (getDoc + saveDogs) para whatsapp/localización/
    // schedules, ambos tocando el MISMO documento days/{dayKey}/profiles/
    // {profile} de forma concurrente (dos queueMicrotask independientes).
    // Eso permitía que uno pisara al otro (ganaba quien escribiera último
    // con una lectura potencialmente desactualizada del otro cambio).
    // Ahora es UN ÚNICO bucle con runTransaction: la lectura y escritura de
    // cada dayKey futuro ocurren atómicamente, y el patch aplicado incluye
    // TODOS los campos relevantes de esta rama a la vez (nombre si hubo
    // renombrado, whatsapp, localización y schedules recurrentes).
    if (workspaceId) {
      queueMicrotask(async () => {
        for (let i = 1; i < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; i++) {
          const weekStart = new Date(firstDateInPresence);
          weekStart.setDate(weekStart.getDate() + i * 7);
          for (const dayDate of getWeekDays(weekStart)) {
            const targetDateKey = formatDateKey(dayDate);
            if (!presenceDayNamesForFields.has(getDayNameFromDateKey(targetDateKey))) continue;
            try {
              const docRef = doc(db, `workspaces/${workspaceId}/days/${targetDateKey}/profiles/${selectedUser}`);
              await runTransaction(db, async (tx) => {
                const docSnap = await tx.get(docRef);
                if (!docSnap.exists()) return;
                const remoteExisting = docSnap.data()?.dogs ?? [];
                const dogIdx = remoteExisting.findIndex(matchesEditedDog);
                if (dogIdx === -1) return;
                // Patch consolidado: nombre (si renombrado) + whatsapp +
                // localización + schedules recurrentes, en una sola escritura.
                const dogsToWrite = remoteExisting.map((d, idx) =>
                  idx === dogIdx
                    ? {
                        ...d,
                        ...(isRename ? { name: finalDogName } : {}),
                        whatsappGroup: newWhatsApp || null,
                        locationUrl: locationUrl || null,
                        schedules: recurringSchedules,
                      }
                    : d
                );
                const sanitizedDogs = sanitizeForFirestore(dogsToWrite);
                tx.set(docRef, { dogs: sanitizedDogs, count: sanitizedDogs.length }, { merge: true });
              });
            } catch (e) {
              console.error('[EDIT_WA_LOC_ERR]', targetDateKey, e?.message);
            }
          }
        }
      });
    }
  }
  // ✅ SIEMPRE: propagar updatedRecurringNotes a Firestore (independientemente de onlyThisWeek)
  // Solo tocar recurringNotes e informativeNote (si no está protegida), sin tocar presencia ni otros campos
  if (onlyThisWeek && affectedWeekdays.size > 0) {
  const workspaceIdNotes = workspaceId;
  if (workspaceIdNotes) {
    queueMicrotask(async () => {
      for (let i = 1; i < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; i++) {
        const weekStart = new Date(firstDateInPresence);
        weekStart.setDate(weekStart.getDate() + i * 7);
        for (const dayDate of getWeekDays(weekStart)) {
          const targetDateKey = formatDateKey(dayDate);
          if (!presenceDayNamesForFields.has(getDayNameFromDateKey(targetDateKey))) continue;
          try {
            const docRef = doc(db, `workspaces/${workspaceIdNotes}/days/${targetDateKey}/profiles/${selectedUser}`);
            await runTransaction(db, async (tx) => {
              const docSnap = await tx.get(docRef);
              if (!docSnap.exists()) return;
              const remoteExisting = docSnap.data()?.dogs ?? [];
              const dogIdx = remoteExisting.findIndex(matchesEditedDog);
              if (dogIdx === -1) return;
              // Patch específico: SOLO recurringNotes + informativeNote (si no protegida)
              const remoteEntry = remoteExisting[dogIdx];
              const noteFieldsRemote = isProtectedNoteException(remoteEntry)
                ? {} // Excepción puntual protegida: NO sobrescribir
                : {
                    informativeNote: resolveNoteForDate(updatedRecurringNotes, targetDateKey),
                    informativeNoteOverride: false,
                  };
              const dogsToWrite = remoteExisting.map((d, idx) =>
                idx === dogIdx
                  ? {
                      ...d,
                      recurringNotes: updatedRecurringNotes,
                      ...noteFieldsRemote,
                    }
                  : d
              );
              const sanitizedDogs = sanitizeForFirestore(dogsToWrite);
              tx.set(docRef, { dogs: sanitizedDogs, count: sanitizedDogs.length }, { merge: true });
            });
          } catch (e) {
            console.error('[EDIT_RECURRING_NOTES_ERR]', targetDateKey, e?.message);
          }
        }
      }
    });
  }
}



  if (!onlyThisWeek) {
    // Fase 2 integridad (C): también consolidado con runTransaction. Este
    // patch ya incluía TODOS los campos (generalDogDataUpdateFuture, que
    // contiene name: finalDogName) en una sola operación por dayKey, pero
    // usaba getDoc()+saveDogs() por separado: si en paralelo el bucle de
    // "onlyThisWeek" (rama distinta) o un reorder tocaban el mismo dayKey,
    // podían pisarse. runTransaction elimina esa ventana de carrera.
    if (workspaceId && propagatedKeysForPersist.size > 0) {
      queueMicrotask(async () => {
        for (const dayKey of propagatedKeysForPersist) {
          try {
            const docRef = doc(db, `workspaces/${workspaceId}/days/${dayKey}/profiles/${selectedUser}`);
            await runTransaction(db, async (tx) => {
            const docSnap = await tx.get(docRef);
              if (!docSnap.exists()) return;
              const remoteExisting = docSnap.data()?.dogs ?? [];

              const dayName = getDayNameFromDateKey(dayKey);

              const shouldBePresent = presenceDayNames.has(dayName);
              const existingEntry = remoteExisting.find(matchesEditedDog);

              let dogsToWrite;
              if (shouldBePresent) {
                // Respetar informativeNoteOverride ya persistido en remoto:
                // si está protegido (true), no tocar su nota; si no, recalcular
                // desde updatedRecurringNotes (igual que en memoria).
                const noteFieldsRemote = isProtectedNoteException(existingEntry)
                  ? {}
                  : {
                      informativeNote: resolveNoteForDate(updatedRecurringNotes, dayKey),
                      informativeNoteOverride: false,
                    };
                if (finalDogName === 'POMPON' || finalDogName === 'MUSHU' || finalDogName === 'PIPPO' || finalDogName === 'MURA' || finalDogName === 'RIO' || finalDogName === 'CLAY' || finalDogName === 'MOWGLY' || finalDogName === 'LARA') {
                  console.log('[NOTE_TRACE]', JSON.stringify({
                    source: 'TX_FUTURE_WEEK',


                    dogId: editedDogId || null,
                    name: finalDogName,
                    dayKey,
                    existingEntryOverride: existingEntry?.informativeNoteOverride ?? null,
                    noteFieldsRemote,
                    ts: Date.now(),
                  }));
                }
                const patch = {

                  ...generalDogDataUpdateFuture,
                  ...noteFieldsRemote,
                  originalOrder: existingEntry?.originalOrder ?? Date.now(),
                  firstAddedDateKey: existingEntry?.firstAddedDateKey
                    ?? presenceDateKeys?.[0] ?? currentSelectedDayKey,
                };
                if (existingEntry) {
                  dogsToWrite = remoteExisting.map((d) =>
                    matchesEditedDog(d) ? { ...d, ...patch } : d
                  );
                } else {
                  dogsToWrite = [
                    ...remoteExisting,
                    { ...patch, ...(editedDogId ? { id: editedDogId } : {}) },
                  ];
                }
              } else {
                // Eliminar solo el perro editado, el resto queda intacto
                dogsToWrite = remoteExisting.filter((d) => !matchesEditedDog(d));
              }

              const sanitizedDogs = sanitizeForFirestore(dogsToWrite);
              tx.set(docRef, { dogs: sanitizedDogs, count: sanitizedDogs.length }, { merge: true });
            });
          } catch (e) {
            console.error('[EDIT_DOG_PERSIST_ERR]', dayKey, e?.message);
          }
        }
      });
    }
  }

  // Aplicar al estado React los estados (completado/urgente) migrados
  // (calculados arriba, ANTES del updater de setDaysList) y disparar el
  // snapshot del historial usando `capturedNewDaysList` (la lista final
  // construida dentro del updater sobre `prevDaysList`), nunca una copia
  // desactualizada capturada por closure.
  setCompletedDogs(finalCompletedDogs);
  if (isRename) {
    setUrgentDogs(finalUrgentDogs);
  }
  recordSnapshotFunc(capturedNewDaysList, finalCompletedDogs, finalUrgentDogs);

  // Persistir los estados migrados en Firestore para cada dayKey afectado
  if (isRename && affectedStateDayKeys.size > 0) {
    const workspaceIdStates = workspaceId;
    if (workspaceIdStates) {
      queueMicrotask(async () => {
        for (const dayKey of affectedStateDayKeys) {
          try {
            const dayCompleted = Object.fromEntries(
              Object.entries(finalCompletedDogs).filter(([k]) => k.startsWith(`${dayKey}-`))
            );
            const dayUrgent = Object.fromEntries(
              Object.entries(finalUrgentDogs).filter(([k]) => k.startsWith(`${dayKey}-`))
            );
            await saveStates(workspaceIdStates, dayKey, dayCompleted, dayUrgent, selectedUser);
          } catch (e) {
            console.error('[EDIT_RENAME_STATES_ERR]', dayKey, e?.message);
          }
        }
      });
    }
  }

  // 🔥 Sincronizar la plantilla (templateDogs) con los datos editados por
  // identidad estable (upsertDogInTemplate). Esto cubre: cambio de WhatsApp,
  // localización, horarios recurrentes, count e isFixed. Si el cliente deja
  // de ser fijo (isFixed=false), se guarda igualmente con ese flag para que
  // mergeByIdentity dentro de templateSync deje de propagarlo a futuro sin
  // necesidad de borrarlo de la plantilla.
  {
    const workspaceIdTemplate = workspaceId;
    if (workspaceIdTemplate) {
      queueMicrotask(() => {
        upsertDogInTemplate(workspaceIdTemplate, selectedUser, {
          id: editedDogId || undefined,
          importKey: editedDogImportKey || undefined,
          name: finalDogName,
          whatsappGroup: newWhatsApp || null,
          locationUrl: locationUrl || null,
          schedules: recurringSchedules,
          count: generalDogDataUpdate.count,
          isFixed: generalDogDataUpdate.isFixed,
          // Propagar explícitamente el historial de notas recurrentes
          // (ya revisado más arriba vía reviseRecurringNotes) a la
          // plantilla. Sin esto, una regla nueva/cerrada aquí nunca
          // llegaría a templateDogs, y extendTemplateWindow seguiría
          // materializando semanas futuras con el histórico ANTERIOR
          // (o sin resolver ninguna nota) en vez de la regla actualizada.
          recurringNotes: updatedRecurringNotes,
        }).catch((e) => console.error('[EDIT_UPSERT_TEMPLATE_ERR]', e?.message));

      });
    }
  }

  // El toast "Perro actualizado" YA se muestra (condicionado a la
  // confirmación real de affectedWeekDateKeys) dentro del queueMicrotask
  // de arriba. No debe repetirse aquí de forma incondicional: mostrarlo
  // también en este punto anularía el requisito de "éxito solo tras
  // confirmación real" (se vería inmediatamente, antes de que termine la
  // persistencia, y aparecería igual aunque la transacción fallase).
  // Caso especial: si affectedWeekDateKeys quedó vacío (ninguna fecha de la
  // semana visible afectada por este perro — p.ej. edición de un cliente no
  // presente en la semana actual), tampoco hay persistencia confirmada que
  // mostrar como éxito: no se muestra ningún toast en ese caso.
};


/* ------------------------------ Reordenaciones ------------------------------ */


export const handleReorderDogs = (
  reorderedDogsForDay,
  selectedDayKey,
  daysList,
  completedDogs,
  urgentDogs,
  setDaysList,
  recordSnapshotFunc,
  toast,
  selectedUser,
  workspaceId,
  scope = 'future'
) => {
  // LOG 1: Entrada con traceId único
  const traceId = `R${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const reorderedSummary = reorderedDogsForDay.map(dog => ({
    identity: getDogIdentity(dog),
    originalOrder: dog.originalOrder
  }));
  console.log('[REORDER_START]', JSON.stringify({
    traceId,
    profile: selectedUser,
    dayKey: selectedDayKey,
    orderCount: reorderedDogsForDay.length,
    order: reorderedSummary,
    timestamp: Date.now()
  }));

  // Fase 2: mapear por identidad estable (id si existe, si no nombre
  // normalizado legacy) en lugar de solo por nombre. Esto evita que un
  // cliente renombrado deje de recibir su nuevo originalOrder en semanas
  // futuras ya cargadas cuyo registro todavía conserva el nombre antiguo
  // (por ejemplo si el rename y el reorder ocurren en momentos distintos).
  const reorderedDogsMap = new Map(
    reorderedDogsForDay.map((dog, index) => [getDogIdentity(dog), index])
  );

  // Bugfix duplicados/listas vacías (rename + reorder casi seguidos):
  // TODA la construcción del nuevo daysList (semana actual reordenada +
  // propagación a semanas futuras ya cargadas) ocurre dentro de un ÚNICO
  // updater funcional de setDaysList, clonando siempre `prevDaysList`
  // (estado más reciente en el momento del commit de React) en vez del
  // `daysList` capturado por closure en el momento de la llamada. Sin esto,
  // un reorder disparado casi a la vez que una edición podía pisar los
  // cambios de la otra operación con una copia desactualizada.
  // Dentro del updater: solo clonado + cálculo síncrono. Sin await, sin
  // llamadas a Firebase, sin mutar `prevDaysList`.
  let capturedNewDaysList = null;

  setDaysList((prevDaysList) => {
    const newDaysList = deepClone(prevDaysList || {});

    // 🔒 Usar composite key: ${selectedUser}::${selectedDayKey}
    const compositeKey = `${selectedUser}::${selectedDayKey}`;
    newDaysList[compositeKey] = reorderedDogsForDay.map((dog, index) => ({
      ...dog,
      originalOrder: index,
    }));

    // 🔒 Propagar a futuras semanas también con composite keys
    if (scope === 'future') {
      for (let i = 1; i < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; i++) {
        const futureDateKey = addWeeksToDateKey(selectedDayKey, i);
        const futureCompositeKey = `${selectedUser}::${futureDateKey}`;

        if (newDaysList[futureCompositeKey] && Array.isArray(newDaysList[futureCompositeKey])) {
          newDaysList[futureCompositeKey] = newDaysList[futureCompositeKey].map((dog) => {
            const identity = getDogIdentity(dog);
            if (reorderedDogsMap.has(identity)) {
              return { ...dog, originalOrder: reorderedDogsMap.get(identity) };
            }
            return dog;
          });
          newDaysList[futureCompositeKey].sort(
            (a, b) => (a.originalOrder || 0) - (b.originalOrder || 0)
          );
        }
      }
    }

    capturedNewDaysList = newDaysList;
    return newDaysList;
  });

  // 🔥 PERSISTENCIA DIRECTA DEL DÍA ACTUAL (selectedDayKey) VÍA FIRESTORE.
  // No depender del autoguardado externo: si hay un lock activo sobre este
  // mismo documento (edición concurrente en curso), el autoguardado puede
  // omitir su escritura y el reorden del día visible se perdería. Aquí se
  // encola en la MISMA cola FIFO (enqueueDocWrite + makeEditLockKey) que usa
  // el autoguardado y las transacciones de weekOfChange, para no pisarse
  // con ellas, y se lee el remoto DENTRO de la transacción (no antes) para
  // evitar condiciones de carrera con escrituras concurrentes.
  if (workspaceId) {
    const todayDocRef = doc(db, `workspaces/${workspaceId}/days/${selectedDayKey}/profiles/${selectedUser}`);
    const todayLockKey = makeEditLockKey(workspaceId, selectedUser, selectedDayKey);

    enqueueDocWrite(todayLockKey, async () => {
      await runTransaction(db, async (tx) => {
        const docSnap = await tx.get(todayDocRef);
        if (!docSnap.exists()) return;

        const remoteExisting = docSnap.data()?.dogs ?? [];
        
        // LOG 2: lectura remota
        const remoteSummary = remoteExisting.map(dog => ({
          identity: getDogIdentity(dog),
          originalOrder: dog.originalOrder
        }));
        const result = remoteExisting.length === 0 ? 'SKIP_EMPTY' : 'WRITE';
        const matchedCount = remoteExisting.filter(dog => reorderedDogsMap.has(getDogIdentity(dog))).length;
        console.log('[REORDER_TX_READ]', JSON.stringify({
          traceId,
          profile: selectedUser,
          dayKey: selectedDayKey,
          exists: true,
          remoteCount: remoteExisting.length,
          remote: remoteSummary,
          result,
          matchedIdentities: matchedCount,
          timestamp: Date.now()
        }));
        
        if (remoteExisting.length === 0) return;

        // Solo se modifica originalOrder de los perros presentes en el
        // reorder (emparejados por identidad estable). El resto de campos
        // de cada perro, y los perros remotos no incluidos en el reorder,
        // quedan intactos tal cual están en remoteExisting.
        const nextDogs = remoteExisting.map((dog) => {
          const identity = getDogIdentity(dog);
          if (reorderedDogsMap.has(identity)) {
            return { ...dog, originalOrder: reorderedDogsMap.get(identity) };
          }
          return dog;
        });

        const sanitizedDogs = sanitizeForFirestore(nextDogs);

        // [REORDER_TX_WRITE]: instrumentación temporal — payload exacto que se
        // entrega a Firestore justo antes de tx.set() para el día actual.
        console.log('[REORDER_TX_WRITE]', JSON.stringify({
          traceId,
          profile: selectedUser,
          dayKey: selectedDayKey,
          payload: sanitizedDogs.map(d => ({
            identity: getDogIdentity(d),
            originalOrder: d.originalOrder
          })),
          timestamp: Date.now()
        }));

        tx.set(todayDocRef, { dogs: sanitizedDogs }, { merge: true });
      });
    }).then(() => {
      // LOG 3: éxito
      console.log('[REORDER_COMMIT_OK]', JSON.stringify({

        traceId,
        profile: selectedUser,
        dayKey: selectedDayKey,
        timestamp: Date.now()
      }));
    }).catch((e) => {
      // LOG 3: error
      console.error('[REORDER_COMMIT_ERROR]', JSON.stringify({
        traceId,
        profile: selectedUser,
        dayKey: selectedDayKey,
        error: e?.message || String(e),
        timestamp: Date.now()
      }));
    });
  }

  // 🔥 PROPAGAR ORDEN A SEMANAS FUTURAS VÍA FIRESTORE (semanas no cargadas en memoria)
  if (workspaceId && scope === 'future') {
    queueMicrotask(async () => {

      for (let i = 1; i < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; i++) {
        const futureDateKey = addWeeksToDateKey(selectedDayKey, i);
        try {
          const docRef = doc(db, `workspaces/${workspaceId}/days/${futureDateKey}/profiles/${selectedUser}`);
          // Fase 2 integridad (A): FIX de identidad + runTransaction.
          // Bug anterior: reorderedDogsMap está indexado por getDogIdentity()
          // (id real si el perro lo tiene, p.ej. tras una importación PDF que
          // ya asigna id a todos los clientes), pero se buscaba con
          // `reorderedDogsMap.has(dog.name)` — comparando un id contra un
          // nombre, que NUNCA coincide. Resultado: knownDogs quedaba vacío y
          // la semana futura no recibía el nuevo orden en absoluto (no lo
          // sobrescribía con datos viejos: simplemente nunca lo actualizaba).
          // Fix: usar getDogIdentity(dog) también al leer remoteExisting.
          // Además, runTransaction evita que esta escritura de orden pise (o
          // sea pisada por) una edición concurrente sobre el mismo dayKey.
          await runTransaction(db, async (tx) => {
            const docSnap = await tx.get(docRef);
            if (!docSnap.exists()) return;

            const remoteExisting = docSnap.data()?.dogs ?? [];
            if (remoteExisting.length === 0) return;

            // Perros del día futuro que aparecen en el reorder → nuevo orden relativo
            const knownDogs = remoteExisting
              .filter(dog => reorderedDogsMap.has(getDogIdentity(dog)))
              .sort((a, b) => reorderedDogsMap.get(getDogIdentity(a)) - reorderedDogsMap.get(getDogIdentity(b)));

            // Nada que actualizar si ningún perro del reorder está en este día futuro
            if (knownDogs.length === 0) return;

            // Perros del día futuro NO en el reorder → conservan su orden relativo previo
            const unknownDogs = remoteExisting
              .filter(dog => !reorderedDogsMap.has(getDogIdentity(dog)));

            // Construir lista final y renumerar secuencialmente sin empates.
            // Solo se toca originalOrder; el resto de campos de cada perro
            // (name, whatsapp, schedules, etc.) queda intacto tal cual está
            // en remoteExisting — el reorder nunca debe pisar otros campos.
            const finalList = [...knownDogs, ...unknownDogs]
              .map((dog, idx) => ({ ...dog, originalOrder: idx }));

            const sanitizedList = sanitizeForFirestore(finalList);
            tx.set(docRef, { dogs: sanitizedList, count: sanitizedList.length }, { merge: true });
          });
        } catch (e) {
          console.error('[REORDER_PROPAGATE_ERR]', futureDateKey, e?.message);
        }
      }
    });

    // 🔥 Actualizar originalOrder en la plantilla para los clientes fijos
    // reordenados. Sin esto, una importación PDF posterior podría restaurar
    // una posición antigua, porque templateDogs no reflejaría el nuevo orden.
    queueMicrotask(() => {
      updateTemplateOrder(workspaceId, selectedUser, reorderedDogsForDay)
        .catch(e => console.error('[REORDER_TEMPLATE_ERR]', e?.message));
    });
  }

  saveToStorage(LOCAL_STORAGE_KEYS.DAYS_LIST, capturedNewDaysList, selectedUser);
  recordSnapshotFunc(capturedNewDaysList, completedDogs, urgentDogs);
};


/* ---------------------------- Cálculo contador día ---------------------------- */
export const calculateDailyDogCount = (
  dateKey,
  daysList,
  urgentDogs,
  getInitialDayData
) => {
  // Buscar composite key primero (profile::dateKey), fallback a plain key
  const compositeKey = Object.keys(daysList || {}).find(k => k.endsWith(`::${dateKey}`));
  const dogsForDay = Array.isArray(daysList?.[compositeKey ?? dateKey])
    ? daysList[compositeKey ?? dateKey]
    : [];
  return dogsForDay.reduce((total, dog) => {
    const isUrgent = urgentDogs[`${dateKey}-${dog.name}`];
    return isUrgent ? total : total + (dog.count || 1);
  }, 0);
};

/* ----------------------- Extensión automática de ventana ---------------------- */
export const extendFixedDogsWindow = (daysList, selectedUser) => {
  // ✅ Guard: Desactivado por feature flag
  if (!ENABLE_AUTO_WINDOW_EXTENSION) {
    console.log("📅 Auto-extension desactivada (feature flag)");
    return daysList;
  }
  
  if (!daysList || Object.keys(daysList).length === 0) {
    console.log("📅 No hay datos para extender la ventana");
    return daysList;
  }

  // Encontrar la fecha más lejana en el futuro
  const dateKeys = Object.keys(daysList).sort();
  const furthestDateKey = dateKeys[dateKeys.length - 1];
  const furthestDate = parseDateKey(furthestDateKey);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calcular semanas restantes
  const weeksRemaining = Math.floor(
    (furthestDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 7)
  );

  console.log(
    `📅 Ventana actual: ${weeksRemaining} semanas (hasta ${furthestDateKey})`
  );

  // Si quedan menos de 4 semanas, extender
  if (weeksRemaining < MIN_WEEKS_REMAINING_FOR_EXTENSION) {
    console.log(
      `⏰ Extendiendo ventana (quedan solo ${weeksRemaining} semanas)...`
    );

    const newDaysList = deepClone(daysList);
    const permanentlyDeletedDogs = loadFromStorage(
      LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS,
      [],
      selectedUser
    ) || [];

    // Recolectar todos los perros fijos únicos
    const fixedDogsMap = new Map();
    Object.keys(daysList).forEach((dateKey) => {
      (daysList[dateKey] || []).forEach((dog) => {
        if (dog.isFixed && !fixedDogsMap.has(dog.name)) {
          fixedDogsMap.set(dog.name, dog);
        }
      });
    });

    console.log(`🐕 Encontrados ${fixedDogsMap.size} perros fijos para extender`);

    // Extender 26 semanas más desde la última fecha
    let extendedCount = 0;
    for (let week = 1; week <= MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; week++) {
      const newDateKey = addWeeksToDateKey(furthestDateKey, week);
      const newDate = parseDateKey(newDateKey);

      if (newDate < furthestDate) continue; // Saltar si es anterior

      // Inicializar el día si no existe
      if (!newDaysList[newDateKey]) {
        newDaysList[newDateKey] = getInitialDayDataForUser(
          newDateKey,
          selectedUser,
          permanentlyDeletedDogs
        );
      }

      // Añadir perros fijos que correspondan a este día de la semana
      const dayName = getDayNameFromDateKey(newDateKey);
      fixedDogsMap.forEach((dog) => {
        const dogIndex = newDaysList[newDateKey].findIndex(
          (d) => d.name === dog.name
        );
        if (dogIndex === -1) {
          // Solo añadir si el perro debería estar este día de la semana
          const firstWeekDays = getWeekDays(parseDateKey(furthestDateKey)).map(
            formatDateKey
          );
          const dogsInFirstDay = daysList[firstWeekDays[0]] || [];
          const shouldIncludeThisDay = dogsInFirstDay.some(
            (d) => d.name === dog.name && getDayNameFromDateKey(newDateKey) === getDayNameFromDateKey(firstWeekDays[0])
          );

          if (shouldIncludeThisDay || dogsInFirstDay.length === 0) {
            // Buscar en qué días de la semana aparece el perro
            const appearingDays = new Set();
            Object.keys(daysList).forEach((key) => {
              (daysList[key] || []).forEach((d) => {
                if (d.name === dog.name) {
                  appearingDays.add(getDayNameFromDateKey(key));
                }
              });
            });

            if (appearingDays.has(dayName)) {
              newDaysList[newDateKey].push({
                ...dog,
                originalOrder: dog.originalOrder || Date.now() + Math.random(),
              });
              extendedCount++;
            }
          }
        }
      });

      // Ordenar
      if (newDaysList[newDateKey]) {
        newDaysList[newDateKey].sort(
          (a, b) => (a.originalOrder || 0) - (b.originalOrder || 0)
        );
      }
    }

    console.log(`✅ Ventana extendida: +${extendedCount} registros de perros`);
    return newDaysList;
  }

  return daysList;
};

/* ----------------------- Batch Cancelaciones (EditDogForm) ---------------------- */
/**
 * Persiste en Firestore, en una ÚNICA transacción atómica, las cancelaciones
 * (añadidas y removidas) de un perro para uno o varios dateKeys, cada uno en
 * su propio documento workspaces/{ws}/days/{dateKey}/profiles/{profile}.
 *
 * Reglas de seguridad:
 * - Todo-o-nada: si falla la lectura/validación/escritura de CUALQUIER
 *   dateKey, no se aplica ningún cambio (runTransaction nativo de Firestore).
 * - Solo toca el campo `urgentDogs` de cada documento (clona el existente y
 *   asigna por bracket notation la clave `${dateKey}-${dogId||dogNameOrId}`).
 *   Nunca toca `dogs` ni `completedDogs`.
 * - Nunca crea documentos: si un doc no existe, aborta toda la transacción.
 * - Para addedDateKeys exige además que el perro exista tanto según la caché
 *   (existsByDateKey, validado ANTES de abrir transacción) como según el
 *   propio snapshot leído dentro de la transacción (defensa contra drift).
 * - Para removedDateKeys solo exige que el documento exista (permite
 *   desmarcar aunque el perro ya no aparezca ese día).
 */
export const persistBatchCanceledDaysToFirebase = async ({
  workspaceId,
  profile,
  dogId,
  dogNameOrId,
  addedDateKeys = [],
  removedDateKeys = [],
  existsByDateKey = {},
}) => {
  if (!workspaceId || !profile) {
    throw new Error('MISSING_WORKSPACE_OR_PROFILE');
  }

  const affected = Array.from(new Set([...(addedDateKeys || []), ...(removedDateKeys || [])]));
  if (affected.length === 0) return;

  // Validación previa (antes de abrir la transacción): un dateKey añadido
  // debe estar confirmado por la caché de la UI como "perro presente".
  for (const dk of addedDateKeys) {
    if (!existsByDateKey[dk]) {
      throw new Error(`DOG_NOT_PRESENT:${dk}`);
    }
  }

  const stateKey = dogId || dogNameOrId;
  const refs = affected.map(dk => doc(db, `workspaces/${workspaceId}/days/${dk}/profiles/${profile}`));

  await runTransaction(db, async (tx) => {
    // 1) Todas las lecturas primero (requisito de runTransaction)
    const snaps = await Promise.all(refs.map(r => tx.get(r)));

    // 2) Validar todo antes de escribir nada
    snaps.forEach((snap, i) => {
      const dk = affected[i];
      if (!snap.exists()) {
        throw new Error(`NO_DOC:${dk}`);
      }
      if (addedDateKeys.includes(dk)) {
        const remoteDogs = snap.data()?.dogs || [];
        const stillPresent = remoteDogs.some(d => (d.id ? d.id === dogId : d.name === dogNameOrId));
        if (!stillPresent) {
          throw new Error(`DOG_NOT_PRESENT_REMOTE:${dk}`);
        }
      }
    });

    // 3) Escrituras: clonar urgentDogs completo de cada doc y asignar solo la clave de este perro/día
    snaps.forEach((snap, i) => {
      const dk = affected[i];
      const key = `${dk}-${stateKey}`;
      const nextUrgentDogs = { ...(snap.data()?.urgentDogs || {}) };
      if (addedDateKeys.includes(dk)) nextUrgentDogs[key] = true;
      if (removedDateKeys.includes(dk)) nextUrgentDogs[key] = false;

      tx.set(refs[i], {
        urgentDogs: nextUrgentDogs,
        _statesUpdatedAt: serverTimestamp(),
      }, { merge: true });
    });
  });
};

/**
 * Aplica todas las cancelaciones (añadidas y removidas) en un único batch.
 * - Persiste PRIMERO en Firestore (transacción única, todo-o-nada).
 * - Solo si la persistencia tiene éxito, actualiza el estado local
 *   (setUrgentDogs UNA vez) y registra snapshot.
 * - Si falla, relanza el error (no toca estado local) para que la UI
 *   (EditDogForm) mantenga el diálogo abierto con el borrador intacto.
 */
export const applyBatchCanceledDays = async (
  dogNameOrId,
  dogId,
  addedDateKeys,
  removedDateKeys,
  urgentDogs,
  setUrgentDogs,
  daysList,
  recordSnapshotFunc,
  completedDogs,
  workspaceId,
  profile,
  existsByDateKey
) => {
  await persistBatchCanceledDaysToFirebase({
    workspaceId,
    profile,
    dogId,
    dogNameOrId,
    addedDateKeys,
    removedDateKeys,
    existsByDateKey,
  });

  // Construir mapa final a partir del estado actual (solo tras éxito remoto)
  const newUrgentDogs = { ...urgentDogs };

  addedDateKeys.forEach(dateKey => {
    const key = `${dateKey}-${dogId || dogNameOrId}`;
    newUrgentDogs[key] = true;
  });

  removedDateKeys.forEach(dateKey => {
    const key = `${dateKey}-${dogId || dogNameOrId}`;
    newUrgentDogs[key] = false;
  });

  setUrgentDogs(newUrgentDogs);
  recordSnapshotFunc(daysList, completedDogs, newUrgentDogs);
};



